import { NgClass, NgStyle } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ComponentRef,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { whenRequestsSettle } from '@iupics-util/tools/requests-settle.util';
import { listenOutsideAngular } from '@iupics-util/tools/zone-listener.util';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApizUIEditTab, NbRequestRequest } from '@compiere-ws/models/apiz-domains';
import {
  CompiereDataGridFilterType,
  CompiereDataGridResponseJSON,
  CompiereDataGridType,
  DataStore,
  DataStoreKey,
  DataStoreName,
  DataStoreRequest,
  DataStoreStatus,
} from '@compiere-ws/models/compiere-data-json';
import { DocserverEntry } from '@compiere-ws/models/docserver-entry-json';
import { DocserverWsResponse } from '@compiere-ws/models/docserverWsResponse';
import { CallInProgressService } from '@compiere-ws/services/call-in-progress/call-in-progress.service';
import { DocServerService } from '@compiere-ws/services/doc-server/doc-server.service';
import { RequestService } from '@compiere-ws/services/request.service';
import { WorkspaceService } from '@web-desktop/components/workspace/components/workspace-ui/workspace.service';
import { SocketService } from '@compiere-ws/services/socket/socket.service';
import BannerComponent from '@iupics-components/message/banner/banner.component';
import { ModalDisplayRequest, ModalDisplayRequester } from '@iupics-components/models/ui-request';
import { OperatorFilterType } from '@iupics-components/models/universal-filter';
import EmailEditorUiComponent from '@iupics-components/specific/window/email-editor-ui/email-editor-ui.component';
import ExportDataUiComponent from '@iupics-components/specific/window/export-data-ui/export-data-ui.component';
import { InfoDialogType } from '@iupics-components/specific/window/info-dialog/info-dialog.component';
import JoinFileUiComponent from '@iupics-components/specific/window/join-file-ui/join-file-ui.component';
import MergetoolComponent from '@iupics-components/specific/window/mergetool/mergetool.component';
import ProcessUiComponent from '@iupics-components/specific/window/process-ui/process-ui.component';
import SpecificWindowUiComponent from '@iupics-components/specific/window/specific-window-ui/specific-window-ui.component';
import StepperUiComponent from '@iupics-components/standard/fields/stepper-ui/stepper-ui.component';
import GridTabInfinityScrollUiComponent from '@iupics-components/standard/grid/grid-tab-infinity-scroll-ui/grid-tab-infinity-scroll-ui.component';
import GridViewUiComponent from '@iupics-components/standard/grid/grid-view-ui/grid-view-ui.component';
import EditTabUiComponent from '@iupics-components/standard/layouts/edit-tab-ui/edit-tab-ui.component';
import AccordionUiComponent from '@iupics-components/standard/layouts/accordion-ui/accordion-ui.component';
import MenuBarDetailUiComponent from '@iupics-components/standard/menu/menu-bar-detail-ui/menu-bar-detail-ui.component';
import MenuSmartbuttonsUiComponent from '@iupics-components/standard/menu/menu-smartbuttons-ui/menu-smartbuttons-ui.component';
import {
  EditViewGetFormIDByTabID,
  EditViewGetFormIDByWindowID,
  EditViewGetProcessIDByTabID,
  EditViewGetProcessIDByWindowID,
} from '@iupics-components/standard/menu/utils/menu.utils';
import PreviewDocComponent, {
  getFileTypeIconClass,
} from '@iupics-components/standard/preview-doc/preview-doc.component';
import { AppConfig } from '@iupics-config/app.config.service';
import { DataStoreService } from '@iupics-manager/managers/data-store/data-store.service';
import { DynamicComponentManagerService } from '@iupics-manager/managers/dynamic-component-manager/dynamic-component-manager.service';
import { KeybindStateManagerService } from '@iupics-manager/managers/keybind-state-manager/keybind-state-manager.service';
import { MessageManagerService } from '@iupics-manager/managers/message/message-manager.service';
import { PrintReportManager } from '@iupics-manager/managers/print-report/print-report-manager.service';
import { RecentItemsManagerService } from '@iupics-manager/managers/recent-items-manager/recent-items-manager.service';
import { SecurityManagerService } from '@iupics-manager/managers/security-manager/security-manager.service';
import { SmartButtonManagerService } from '@iupics-manager/managers/smart-button-manager/smart-button-manager.service';
import { UICreatorService } from '@iupics-manager/managers/ui-creator/ui-creator.service';
import { WindowFactoryUtils } from '@iupics-manager/managers/ui-creator/window-factory/window-factory-utils';
import { AbstractDataContainer } from '@iupics-manager/models/abstract-datacontainer';
import { AbstractDynamicComponent } from '@iupics-manager/models/abstract-dynamic-component';
import { AbstractDynamicView } from '@iupics-manager/models/abstract-dynamic-view';
import { DataConflict } from '@iupics-manager/models/data-conflict';
import { DynamicComponent } from '@iupics-manager/models/dynamic-component';
import { Global } from '@iupics-manager/models/global-var';
import { IupicsZoomInfo, IupicsZoomTarget } from '@iupics-manager/models/iupics-data';
import { IupicsCellEvent, IupicsEvent, IupicsTypeEvent } from '@iupics-manager/models/iupics-event';
import { IupicsMessage } from '@iupics-manager/models/iupics-message';
import { MongoSearchQueryOperator, MongoSearchQueryPart } from '@iupics-manager/models/mongo-search';
import { ToolbarProcess } from '@iupics-manager/models/toolbar-item';
import { DynamicContainerDirective } from '@iupics-util/directives/dynamic-container.directive';
import { createComponent, getComponentType } from '@iupics-util/tools/component-cache-loader';
import { LogicEvaluator } from '@iupics-util/tools/logic-evaluator';
import { ApizEventType } from '@iupics/apiz-grid';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IupicsMenuType } from '@web-desktop/models/menu-item-ui';
import { environment } from 'environments/environment';
import { cloneDeep, debounce, has } from 'lodash';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { ScrollPanel, ScrollPanelModule } from 'primeng/scrollpanel';
import { Sidebar, SidebarModule } from 'primeng/sidebar';
import { TooltipModule } from 'primeng/tooltip';
import { BehaviorSubject, of, Subject, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import AdditionalInfoUiComponent from '../additional-info-ui/additional-info-ui.component';
import BladeUiComponent from '../blade-ui/blade-ui.component';
import ModalUiComponent, { ModalContentType } from '../modal-ui/modal-ui.component';
import { injectEditViewService, provideEditViewService } from './edit-view.service';
import { EditViewUtils } from './utils/edit-view.utils';

@Component({
  selector: 'iu-edit-view-ui',
  templateUrl: './edit-view-ui.component.html',
  styleUrls: ['./edit-view-ui.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    NgStyle,
    ButtonModule,
    GridViewUiComponent,
    MenuBarDetailUiComponent,
    ProgressBarModule,
    MenuSmartbuttonsUiComponent,
    StepperUiComponent,
    BannerComponent,
    ScrollPanelModule,
    DynamicContainerDirective,
    MergetoolComponent,
    SidebarModule,
    forwardRef(() => ModalUiComponent),
    JoinFileUiComponent,
    EmailEditorUiComponent,
    ExportDataUiComponent,
    ProcessUiComponent,
    TranslateModule,
    TooltipModule,
  ],
  host: {
    '[style.--blade-nb-cols]': 'nbCols()',
  },
  providers: [SmartButtonManagerService, provideEditViewService()],
})
export default class EditViewUiComponent
  extends AbstractDynamicView
  implements OnInit, AfterViewInit, OnDestroy, ModalDisplayRequester
{
  readonly #store = inject(DataStoreService);
  readonly #socketService = inject(SocketService);
  readonly #printReportManager = inject(PrintReportManager);
  readonly #renderer = inject(Renderer2);
  readonly #zone = inject(NgZone);
  readonly #connectorService = inject(SecurityManagerService);
  readonly #translateService = inject(TranslateService);
  readonly #uiCreatorService = inject(UICreatorService);
  readonly #keybindStateManager = inject(KeybindStateManagerService);
  readonly #docServerService = inject(DocServerService);
  readonly #messageManager = inject(MessageManagerService);
  readonly #riManager = inject(RecentItemsManagerService);
  readonly #config = inject(AppConfig);
  readonly #callInProgressService = inject(CallInProgressService);
  readonly #dynamicComponentManager = inject(DynamicComponentManagerService);
  readonly #smartButtonManagerService = inject(SmartButtonManagerService);
  readonly #editViewService = injectEditViewService();
  readonly #requestService = inject(RequestService);
  readonly #workspaceService = inject(WorkspaceService);
  /**
   * utilisé pour modifier le style quand une formModal est ouverte par un composant autre que bouton
   */
  customFormModalBodyCss = undefined;
  submitted: boolean;
  editTabs: EditTabUiComponent[] = [];
  noData = false;
  scrollPanelid: string;
  isLoading = false;
  /** save done: the save button shows a check a moment (micro-interactions.scss) */
  justSaved = false;
  /** a save request is running (save button disabled, spinner) */
  get isSaving(): boolean {
    return !this.#callInProgressService.isSubscriptionDone(this.uuid, 'editview.saveData');
  }
  #justSavedTimer: ReturnType<typeof setTimeout> | undefined;
  private _conflictsResult: DataConflict = {
    refreshAuto: false,
    mustRefresh: false,
    hasConflicts: false,
    dataChanged: {},
    dataConflict: {},
  };
  get conflictsResult(): DataConflict {
    return this._conflictsResult;
  }

  set conflictsResult(conflictsResult: DataConflict) {
    this._conflictsResult = conflictsResult;
    if (conflictsResult.refreshAuto) {
      setTimeout(() => {
        this.refreshData();
      }, 2000);
    }
  }

  get nbConflicts(): number {
    const nb = Object.keys(this.conflictsResult.dataConflict).length;
    if (nb <= 0) {
      this.conflictsResult.hasConflicts = false;
    }
    return nb;
  }

  wantToMergeConflicts = false;
  oldDataStoreKey: DataStoreKey;
  displayJoinFilesPanel = false;
  isZoomEditView = false;
  isStepperVisible = false;
  additionalInfoComponent: AdditionalInfoUiComponent;
  nbUploadedFiles = 0;
  nbRequest = 0;
  windowType = IupicsMenuType.WINDOW;
  /*keep last event to update the state of additionalInfo if he isnt created yet*/
  lastEvent: IupicsEvent;
  @ViewChild('vcrSpecific', { read: ViewContainerRef, static: true })
  vcrSpecific: ViewContainerRef;
  @ViewChild('specificModal', { read: ModalUiComponent, static: true })
  specificModal: ModalUiComponent;
  @ViewChild('exportDataModal')
  exportDataModal: ModalUiComponent;
  @ViewChild('vcrAudit', { read: ViewContainerRef })
  vcrAudit: ViewContainerRef;
  @Input()
  linkedComponents;
  @Input()
  isAdditionalGenerated = false;
  /*permet de savoir si l'edit attend un scroll call par le resizede la grid */
  isWaitingScroll = false;
  isAdditionalOpened: boolean;
  zoomTarget: IupicsZoomTarget;
  isZoomTarget = false;
  filterZoomTarget = [];
  zoomTargetData: ApizUIEditTab[]; // todo: pourquoi c'est un tableau ??
  GridViewVisibility = 'hidden';
  //   displaySearch = false;
  searchLinkedComponent;

  //   specificDisplaySearch = false;
  specificSearchLinkedComponent;

  isSidebarOpen = false;
  notes: any[];

  @ViewChild(MenuBarDetailUiComponent, { static: true })
  menuBarDetailComponent: MenuBarDetailUiComponent;
  @ViewChild('closeButton')
  closeButtonElement: ElementRef;
  @ViewChild('openButton')
  openButton: ElementRef;
  @ViewChild('editViewElement', { static: true })
  editViewElement: ElementRef;
  @ViewChild('gridUi', { static: true })
  gridUiElement: ElementRef;
  @ViewChild('additionalInfoVcr', { read: ViewContainerRef })
  additionalInfoVcr: ViewContainerRef;

  @ViewChild(ScrollPanel, { static: true })
  scrollPanel: ScrollPanel;

  @ViewChild('gridViewUi', { static: false })
  gridViewUi: GridViewUiComponent;
  @ViewChild('sideBar') sideBar: Sidebar;

  @ViewChild('sideBarVcr', { read: ViewContainerRef })
  sideBarVcr: ViewContainerRef;

  @ViewChild('sideBladeVcr', { read: ViewContainerRef })
  sideBladeVcr: ViewContainerRef;
  @ViewChild('sideBladeContainer', { static: false })
  sideBladeContainerEl: ElementRef<HTMLDivElement>;
  sideBladeTitle;
  sideBladeIconClass;
  /** attachments the side preview can step through (only when the opener gave the list) */
  previewFiles: { fileName: string; urlFile: any }[] = [];
  previewIndex = -1;
  @ViewChild('stepperDiv', { static: false }) stepperElt: ElementRef<HTMLDivElement>;

  changingStepper = new Subject<any>();
  changingMenuBar = new BehaviorSubject<any>(DataStoreStatus.NEWRECORD);

  sideComponentRef: ComponentRef<any> = null;
  private _currentDataStoreKey: DataStoreKey;
  get currentDataStoreKey(): DataStoreKey {
    return this._currentDataStoreKey;
  }

  set currentDataStoreKey(currentDataStoreKey: DataStoreKey) {
    this._currentDataStoreKey = currentDataStoreKey;
    this.container.notifyUrlChange();
    /*mettre à jour le datastorekey des editTabs */
    if (this.editTabs && this.editTabs.length > 0) {
      for (const editTab of this.editTabs) {
        editTab.dataStoreKey = currentDataStoreKey;
      }
    }
    this.currentDataStoreKeyChange?.next(currentDataStoreKey);
  }
  /** emits on every record change (navigation, new, save): lets open popups follow the record */
  readonly currentDataStoreKeyChange = new Subject<DataStoreKey>();

  gridTabIdSelected: number;
  stepItems: MenuItem[] = [];

  displayEmailEditor = false;
  displayExportDataModal = false;
  displayCustoModal = false;
  exportDataOptions: any[] = [];
  showAuditsPanel = false;

  displayProcessUI = false;
  displayProcessUI_sourceComponentData: any;
  displayFormUI = false;
  processId: number;
  formId: number;

  isStepperEmpty: boolean;

  isSplitView = false;
  specificWindowTitle: string;
  table_id: number;
  isProcessLaunch = false;
  priceHistoryFormId: number;
  modalClass = 'p-col-10 p-md-10 p-lg-10';

  searchPanelValidation: string;

  //#region enum for template
  ModalContentType = ModalContentType;
  DatastoreStatus = DataStoreStatus;
  //#endregion
  isResizing = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private resizeUnlisteners: (() => void)[] = [];
  // Incremented by each retrieveData call, used to drop replies for a previous record
  private retrieveDataSeq = 0;

  nbCols = signal(2);

  //#region process toolbar
  processes = this.#editViewService.processes;
  //#endregion

  private dataMergeSub: Subscription;

  ngOnInit(): void {
    this.cssClass += ' ui-g';
    this.scrollPanelid = 'scrollPanelEditView' + this.tabId;
    this.#keybindStateManager.splitViewEmitter
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: boolean) => (this.isSplitView = value));
    this.onResizeSideBladeEnd = debounce(this.onResizeSideBladeEnd, 100);
    this.listenSmartButtonClick();
    // normal behavior is to have the first tab as index === 0
    this.nbCols.set(this.children[0].data.editData.nbCol);
  }

  ngAfterViewInit(): void {
    this.#listenSectionScroll();
    this.#watchInfoBarWrap();
    if (this.zoomInfo) {
      this.generateZoom();
    }
    if (this.zoomTarget) {
      this.filterZoomTarget.push(this.zoomTarget.record_id);
      this.isZoomTarget = true;
    }
    if (!this.zoomTarget && !this.zoomInfo) {
      this.buildChildren();
    }
    document.body.style.cursor = 'auto';
    this.#editViewService.setEditViewData(this.data);
  }

  openAdditionalInfo(): void {
    const item: DynamicComponent = {
      container: this.container,
      DOMParentComponent: this,
      linkedComponents: this.linkedComponents,
      component: 'AdditionalInfoUiComponent',
      cssClass: 'p-col-12',
      isCssOnComponent: true,
      windowId: this.data.AD_Window_ID,
      tabId: this.tabId,
    };
    this.componentEmitter.emit({
      type: IupicsTypeEvent.showAdditionalInfo,
      item: item,
    });
  }

  getCurrentDataStoreKey(): DataStoreKey {
    return this.currentDataStoreKey;
  }

  minimize(): void {
    this.isAdditionalOpened = false;
    this.additionalInfoWidth = '2em';
    // this.updateSmartButton();
    this.scrollTo();
  }

  resize(): void {
    this.isAdditionalOpened = true;
    if (Global.isMobile()) {
      this.additionalInfoWidth = '100%';
    } else {
      this.additionalInfoWidth =
        this.additionalInfoWidthExpanded * (this.container as BladeUiComponent).editTabSize + 'em';
    }
    if (!this.isAdditionalGenerated) {
      this.openAdditionalInfo();
      this.isAdditionalGenerated = true;
    } else {
      for (const editTab of this.additionalInfoComponent.editTabs) {
        if (!editTab.DOMChildrenComponent[0]?.data) {
          continue;
        }

        const gridTabInfinityScroll = (editTab.DOMChildrenComponent[0] as GridViewUiComponent)
          .GridTabInfinityScrollUiComponent;
        if (gridTabInfinityScroll) {
          setTimeout(() => {
            gridTabInfinityScroll.grid.api.refreshHeader();
            gridTabInfinityScroll.columnResize();
          }, 0);
        }
      }
    }
    // this.updateSmartButton();
    this.scrollTo();
  }

  scrollTo(): void {
    if (this?.container?.scrollToScrollingPosition) {
      (this.container as BladeUiComponent).scrollToScrollingPosition(this.editViewElement?.nativeElement);
    }
  }

  onZoomTargetGridAfterViewInit(gridTab: GridTabInfinityScrollUiComponent): void {
    if (!this.zoomTarget || !this.isZoomTarget) {
      return;
    }

    const compiereRequest = cloneDeep(gridTab.currentRequest);
    compiereRequest.startRow = 0;
    compiereRequest.endRow = 1;
    const request: DataStoreRequest = {
      windowId: (this.container as BladeUiComponent).infoComponent.windowId,
      parent_constraint: this.zoomTarget.record_id.replace(/,/g, '='),
      compiereRequest,
    };

    // no seq guard here: this reply is the only place that builds the zoom-target children
    this.subscriptions.push(
      this.#store.getWindowSingleData(request).subscribe((dataStore: DataStore) => {
        if (dataStore) {
          this.currentDataStoreKey = this.#store.generateDataStoreKey(
            (this.container as BladeUiComponent).infoComponent.windowId,
            this.tabId,
            dataStore.data['Data_UUID'],
            this.zoomTarget.record_id.replace(/,/g, '=')
          );

          this.updateSmartButton(dataStore);
          this.updateNbFileAttached(dataStore);
          this.updateNbRequest(dataStore);
          this.buildChildren();
        } else {
          this.removeComponent();
          throw new Error(this.#translateService.instant('notification.zoomInfoNotValid'));
        }
      })
    );
  }

  buildChildren(): void {
    for (const child of this.children) {
      WindowFactoryUtils.addContainerComponent({
        parent: this,
        item: child,
        isCssOnComponent: child.container ? child.isCssOnComponent : false,
      });
    }

    if (this.container as BladeUiComponent) {
      (this.container as BladeUiComponent).updateBlade();
    }
  }

  addTabToEditView(editTabUi: EditTabUiComponent): void {
    this.editTabs.push(editTabUi);
    if (this.editTabs.length === 1) {
      this.editTabs[0].isTopEditTab = true;
      this.isStepperVisible = this.editTabs[0].data.isDocumentTab;
      if (
        (
          this.DOMParentComponent.DOMChildrenComponent.find(
            (child) => child instanceof EditViewUiComponent
          ) as EditViewUiComponent
        ).isStepperVisible === true &&
        (this.DOMParentComponent.DOMChildrenComponent.find(
          (child) => child instanceof EditViewUiComponent
        ) as EditViewUiComponent) !== this
      ) {
        this.isStepperVisible = true;
        this.isStepperEmpty = true;
      }
    }
  }

  checkData(): boolean {
    return this.#store.isWindowNewData(this.currentDataStoreKey)
      ? false
      : this.#store.isWindowDataSYNC(this.currentDataStoreKey);
  }

  /** unsaved changes: Save, Discard or Stay (stay answers nothing: the record and its changes are kept) */
  #askUnsavedChanges(kind: 'close' | 'change', onSave: () => void, onDiscard: () => void): void {
    Global.infoDialog.message = {
      summary: this.#translateService.instant(`infodialog.dialogs.checkBefore.${kind}.title`),
      detail: this.#translateService.instant(`infodialog.dialogs.checkBefore.${kind}.message`),
    };
    Global.infoDialog.dialogType = InfoDialogType.CONFIRM_YESNO;
    Global.infoDialog.confirmLabel = this.#translateService.instant('infodialog.save');
    Global.infoDialog.cancelLabel = this.#translateService.instant('infodialog.discard');
    Global.infoDialog.stayLabel = this.#translateService.instant('infodialog.stay');
    Global.infoDialog.showInfoDialog();
    const subscriptions = [
      Global.infoDialog.confirm.subscribe(() => {
        subscriptions.forEach((sub) => sub.unsubscribe());
        onSave();
      }),
      Global.infoDialog.cancel.subscribe(() => {
        subscriptions.forEach((sub) => sub.unsubscribe());
        onDiscard();
      }),
      Global.infoDialog.dismiss.subscribe(() => subscriptions.forEach((sub) => sub.unsubscribe())),
    ];
  }

  checkBeforeClose(event?: Event, onlyEditView = true, callback?: (...value: any) => void): void {
    let isOK = false;
    try {
      isOK = this.checkData();
    } catch (e) {
      console.error(e);
      isOK = true;
    }
    if (!this.isReadOnly && !isOK) {
      this.#askUnsavedChanges(
        'close',
        () =>
          this.beforeSave(event).then(() => {
            if (
              this.conflictsResult &&
              this.conflictsResult.hasConflicts === false &&
              this.conflictsResult.mustRefresh === false
            ) {
              if (onlyEditView) {
                this.removeComponent();
              } else if (callback) {
                callback();
              }
            }
          }),
        () => {
          if (this.editTabs[0].dataStored.status !== DataStoreStatus.NEWRECORD) {
            this.subscriptions.push(
              this.#store.syncWithRemoteWindowData(this.currentDataStoreKey).subscribe((res) => {
                this.notifierLinkedComponent.next({
                  type: IupicsTypeEvent.REFRESH_GRID,
                });
                if (onlyEditView) {
                  this.removeComponent();
                } else if (callback) {
                  callback();
                }
              })
            );
          } else {
            this.#store.deleteDataFromStoreOnly(this.currentDataStoreKey);
            if (onlyEditView) {
              this.removeComponent();
            } else if (callback) {
              callback();
            }
          }
        }
      );
    } else if (onlyEditView) {
      this.removeComponent();
    } else if (callback) {
      callback();
    }
    event?.stopPropagation();
  }

  removeComponent(fromSelectChange = false): void {
    if (!fromSelectChange) {
      if (this.isZoomEditView && this.linkedComponents?.[0]?.isZoom) {
        this.linkedComponents[0].isZoom = false;
      }

      if (this.isZoomTarget) {
        this.linkedComponents[0].toggleSmartButton(this.zoomTarget.buttonId);
      }
    }

    const itemToRemove: DynamicComponent = {
      container: this.container,
      DOMParentComponent: this.DOMParentComponent,
      component: this,
      tabId: this.tabId,
      windowId: (this.container as BladeUiComponent).infoComponent.windowId,
    };

    this.componentEmitter.emit({
      type: IupicsTypeEvent.removeChildEvent,
      item: itemToRemove,
    });

    this.notifierLinkedComponent.next({
      type: IupicsTypeEvent.removeChildEvent,
    });
  }

  onChildUpdate(event: IupicsEvent): void {
    switch (event.type) {
      case IupicsTypeEvent.collapseEvent:
        for (const child of this.DOMChildrenComponent) {
          child.onChildUpdate(event);
        }
        this.collapseElement(event);
        break;

      case IupicsTypeEvent.expandEvent:
        for (const child of this.DOMChildrenComponent) {
          child.onChildUpdate(event);
        }
        this.expandElement();
        break;

      case IupicsTypeEvent.showAdditionalInfo: {
        const additionalInfo = this.DOMChildrenComponent.find((child) => child instanceof AdditionalInfoUiComponent);
        event.item.dataStoreKey = this.currentDataStoreKey;
        additionalInfo.onChildUpdate(event);
        break;
      }

      case IupicsTypeEvent.selectDataChange:
        if (
          this.DOMChildrenComponent?.[0]?.DOMChildrenComponent?.[0]?.DOMChildrenComponent?.[0]
            ?.DOMChildrenComponent?.[0] instanceof SpecificWindowUiComponent
        ) {
          this.DOMChildrenComponent?.[0].DOMChildrenComponent?.[0].DOMChildrenComponent?.[0].DOMChildrenComponent?.[0].onSiblingUpdate(
            event
          );
        }
        break;
    }
  }

  onSiblingUpdate(event: IupicsEvent): void {
    switch (event.type) {
      case IupicsTypeEvent.collapseEvent:
      case IupicsTypeEvent.expandEvent:
        if (!this.isAdditionalGenerated) {
          // keep last change so additionnalInfo can have the same state as edit
          this.lastEvent = cloneDeep(event);
        }

        for (const editTab of this.editTabs) {
          if (editTab.DOMChildrenComponent[0]) {
            editTab.DOMChildrenComponent[0].onSiblingUpdate(event);
          }
        }

        break;

      case IupicsTypeEvent.selectZoomChange:
        this.refreshZoom(event.item.zoomInfo);
        break;

      case IupicsTypeEvent.selectDataChange:
        this.retrieveData(event.item.dataStoreKey);
        // preview of the first file: after the record (secondary)
        this.#afterPreview();
        break;

      case IupicsTypeEvent.checkBeforeChange:
        if (this.currentDataStoreKey) {
          this.checkBeforeChange(event);
        } else {
          this.notifierLinkedComponent.next({
            type: IupicsTypeEvent.triggerAfterChange,
            item: event.item,
          });
        }
        break;
    }

    this.onChildUpdate(event);
  }

  checkBeforeChange(event: IupicsEvent): void {
    const isOK = this.checkData();
    if (!isOK) {
      const ctx = this.#connectorService.getIupicsUserContext();
      if (has(ctx, 'AutoCommit') && ctx.AutoCommit === 'Y' && !this.#store.isWindowNewData(this.currentDataStoreKey)) {
        this.beforeSave(document.createEvent('Event')).then(() => {
          if (
            this.conflictsResult &&
            this.conflictsResult.hasConflicts === false &&
            this.conflictsResult.mustRefresh === false
          ) {
            this.notifierLinkedComponent.next({
              type: IupicsTypeEvent.triggerAfterChange,
              item: event.item,
            });
          }
        });
      } else {
        this.#askUnsavedChanges(
          'close',
          () =>
            this.beforeSave(document.createEvent('Event')).then(() => {
              if (
                this.conflictsResult &&
                this.conflictsResult.hasConflicts === false &&
                this.conflictsResult.mustRefresh === false
              ) {
                this.notifierLinkedComponent.next({
                  type: IupicsTypeEvent.triggerAfterChange,
                  item: event.item,
                });
              }
            }),
          () => {
            if (this.editTabs[0].dataStored.status !== DataStoreStatus.NEWRECORD) {
              this.subscriptions.push(this.#store.syncWithRemoteWindowData(this.currentDataStoreKey).subscribe());
            } else {
              this.#store.deleteDataFromStoreOnly(this.currentDataStoreKey);
            }
            this.notifierLinkedComponent.next({
              type: IupicsTypeEvent.triggerAfterChange,
              item: event.item,
            });
          }
        );
      }
    } else {
      this.notifierLinkedComponent.next({
        type: IupicsTypeEvent.triggerAfterChange,
        item: event.item,
      });
    }
  }

  collapseElement(event: IupicsEvent): void {
    // this.menuBarDetailComponent.setVisibleButton(false);
    for (const tab of this.editTabs) {
      tab.collapseTab();
      if (event.item && tab.tabId !== event.item.tabId) {
        tab.isCollapsed = true;
      }
    }
    /*dont close if additional is just opened for the first time */
    if (
      this.closeButtonElement &&
      (!event.item || !(event.item.DOMParentComponent instanceof AdditionalInfoUiComponent))
    ) {
      this.closeButtonElement?.nativeElement.click();
    }
    // this.smartButtons.resizeSmartButton(event);
  }

  expandElement(): void {
    for (const tab of this.editTabs) {
      tab.expandTab();
    }
    // this.smartButtons.resizeSmartButton(event);
  }

  #nbFilesSub: Subscription | undefined;
  #nbRequestSub: Subscription | undefined;
  /** badges / preview waiting for the record's requests (one pending of each) */
  #cancelBadges: (() => void) | undefined;
  #cancelPreview: (() => void) | undefined;
  #previewSub: Subscription | undefined;

  /**
   * Badges (attachments, requests) and the first file preview: after the record and its child tabs are read, so
   * their requests no longer queue before what the form waits for. Dropped if another record is shown meanwhile.
   */
  #afterRecordShown(fn: () => void, cancelPrevious?: () => void): () => void {
    const seq = this.retrieveDataSeq;
    cancelPrevious?.();
    return whenRequestsSettle(this.#zone, () => {
      if (seq === this.retrieveDataSeq) fn();
    });
  }

  updateNbFileAttached(dataStored?: DataStore): void {
    let dataStoredUsed = dataStored;
    if (!dataStoredUsed) {
      dataStoredUsed = this.#store.getStore(this.editTabs[0].dataStoreKey, DataStoreName.CURRENT) as DataStore;
    }
    if (dataStoredUsed && dataStoredUsed.status !== DataStoreStatus.NEWRECORD) {
      const samples = this.#docServerService.initBaseSearch(this.currentDataStoreKey, this.data, this.table_id);
      // the answer of the record shown only: a previous record's late answer is dropped
      const seq = this.retrieveDataSeq;
      this.#nbFilesSub?.unsubscribe();
      this.#nbFilesSub = this.#docServerService.getNbDocuments(samples, MongoSearchQueryOperator.OR).subscribe((nb) => {
        if (seq === this.retrieveDataSeq) this.nbUploadedFiles = nb;
      });
    } else {
      this.nbUploadedFiles = 0;
    }
  }

  updateNbRequest(dataStored?: DataStore): void {
    let dataStoredUsed = dataStored;
    if (!dataStoredUsed) {
      dataStoredUsed = this.#store.getStore(this.editTabs[0].dataStoreKey, DataStoreName.CURRENT) as DataStore;
    }
    if (
      dataStoredUsed &&
      dataStoredUsed.status !== DataStoreStatus.NEWRECORD &&
      this.currentDataStoreKey.recordId.split(',').length == 2
    ) {
      let recordId: string[] | number = this.currentDataStoreKey.recordId.split(',');
      recordId = recordId.length > 1 ? (isNaN(parseInt(recordId[1], 10)) ? -1 : parseInt(recordId[1], 10)) : -1;
      if (recordId !== -1) {
        const request: NbRequestRequest = {
          recordId: recordId,
          tableId: this.data.AD_Table_ID,
          salesRepId: this.getCurrentContext()['##AD_User_ID'],
          roleId: this.getCurrentContext()['#AD_Role_ID'],
        };
        // the answer of the record shown only: a previous record's late answer is dropped
        const seq = this.retrieveDataSeq;
        this.#nbRequestSub?.unsubscribe();
        this.#nbRequestSub = this.#requestService.getNbRequests(request).subscribe((nb: CompiereDataGridResponseJSON) => {
          if (seq === this.retrieveDataSeq) this.nbRequest = nb.data[0]['NBRQ'];
        });
      } else {
        this.nbRequest = 0;
      }
    } else {
      this.nbRequest = 0;
    }
  }

  updateSmartButton(dataStored?: DataStore): void {
    const dataStoredUsed =
      dataStored ?? (this.#store.getStore(this.currentDataStoreKey, DataStoreName.CURRENT) as DataStore);
    this.#smartButtonManagerService.update(
      this.tabId,
      dataStoredUsed.status !== DataStoreStatus.NEWRECORD ? dataStoredUsed.key.recordId : undefined
    );
  }

  toggleSmartButton(key: number): void {
    this.#smartButtonManagerService.toggleSmartButton(key);
  }

  #afterPreview(): void {
    this.#cancelPreview = this.#afterRecordShown(() => this.getFirstFileAttached(), this.#cancelPreview);
  }

  getFirstFileAttached(): void {
    if (!(this.data?.fileOpening && this.data?.fileOpeningFilters) || Global.isMobile()) {
      this.closeSideBarAndBlade();
      return;
    }
    let fileOpeningFiltersParsed = JSON.parse(this.data.fileOpeningFilters);
    if (!Array.isArray(fileOpeningFiltersParsed)) {
      fileOpeningFiltersParsed = [fileOpeningFiltersParsed];
    }
    const samples = this.#docServerService.initBaseSearch(this.currentDataStoreKey, this.data, this.table_id);
    const searchQuery = this.#docServerService.createQuery(samples, MongoSearchQueryOperator.OR);
    for (const filter of fileOpeningFiltersParsed) {
      searchQuery.queryPart.addExpression(
        MongoSearchQueryPart.createQueryPart(filter.Key, filter.Value, MongoSearchQueryOperator.EQUALS)
      );
    }
    searchQuery.queryPart.addExpression(
      MongoSearchQueryPart.createQueryPart(
        'META|FILENAME',
        '.*.(pdf|xml|png|jpeg|jpg|bmp|gif|webp|svg|ico|xlsx|sh|txt|csv)',
        MongoSearchQueryOperator.REGEX
      )
    );

    // only the name and url of the first file are read: no count, tags or AD_Attachment rows (each read again)
    searchQuery.entriesOnly = true;
    let recordId: any = this.currentDataStoreKey.recordId.split(',');
    recordId = recordId.length > 1 ? (isNaN(parseInt(recordId[1], 10)) ? recordId[1] : parseInt(recordId[1], 10)) : -1;
    const seq = this.retrieveDataSeq;
    this.#previewSub?.unsubscribe();
    this.#previewSub = this.#docServerService
      .advancedSearchDocuments(
        searchQuery,
        { limit: 1, start: 0, attachmentInteraction: false },
        this.tabId,
        this.table_id,
        recordId
      )
      .subscribe((response: DocserverWsResponse) => {
        // a previous record's late answer must not show its file
        if (seq !== this.retrieveDataSeq) return;
        if (response?.entries[0]) {
          const hit: DocserverEntry = response?.entries[0];
          if (hit && this.#docServerService.hasPreviewByName(hit.fileName)) {
            this.showPreview({
              fileName: hit.fileName,
              urlFile: hit.url,
            });
          }
        } else {
          this.closeSideBarAndBlade();
        }
      });
  }

  // clic on grid: OLD and WS
  retrieveData(dataStoreKey: DataStoreKey): void {
    // finished subscriptions dropped: the list grew with every record shown (and kept old records alive)
    this.subscriptions = this.subscriptions.filter((s) => !s.closed);
    if (this.currentDataStoreKey !== undefined && environment.constant.mergeLevel !== 0) {
      this.#socketService.closeDataChannel(this.currentDataStoreKey.windowId + '-' + this.currentDataStoreKey.recordId);
      this.dataMergeSub?.unsubscribe();
    }
    this.currentDataStoreKey = cloneDeep(dataStoreKey);
    // conflicts of the previous record never carry over (its "Verify" would merge them into this one); the opening
    // check that reset them is skipped for a record fresh from the server
    this._conflictsResult = { refreshAuto: false, mustRefresh: false, hasConflicts: false, dataChanged: {}, dataConflict: {} };
    this.wantToMergeConflicts = false;
    // Replies for a previously requested record are ignored (late reply must not apply the old record)
    const seq = ++this.retrieveDataSeq;
    const request: DataStoreRequest = {
      windowId: this.currentDataStoreKey.windowId,
      record_id: this.currentDataStoreKey.recordId,
      parent_constraint: this.currentDataStoreKey.parentId,
      compiereRequest: {
        windowType: CompiereDataGridType.WINDOW,
        entityId: this.currentDataStoreKey.tabId,
        startRow: 0,
        endRow: 1,
        validation: this.getTabWhereclause(),
        windowCtx: this.getCurrentContext(),
      },
    };

    if (this.#store.isWindowNewData(this.currentDataStoreKey)) {
      this.subscriptions.push(
        this.#store.newRecord(this.currentDataStoreKey, this.getCurrentContext(true, true)).subscribe((dataStored) => {
          if (seq !== this.retrieveDataSeq) {
            return;
          }
          if ((this.zoomTarget && this.zoomTarget.zoomRequest) || (this.zoomInfo && this.zoomInfo.zoomRequest)) {
            const zoomRequest =
              this.zoomTarget && this.zoomTarget.zoomRequest ? this.zoomTarget.zoomRequest : this.zoomInfo.zoomRequest;
            dataStored.data['AD_Table_ID'] = zoomRequest.ad_table_id;
            dataStored.data['Record_ID'] = zoomRequest.record_id;
          }
          this.updateEditTabsData(dataStored);
        })
      );
    } else {
      let loaded: DataStore | undefined;
      new Promise<void>((resolve) => {
        this.subscriptions.push(
          this.#store.getWindowSingleData(request).subscribe((dataStored: DataStore) => {
            // Stale reply: leave the promise pending so the following steps are skipped too
            if (seq !== this.retrieveDataSeq) {
              return;
            }
            loaded = dataStored;
            this.updateEditTabsData(dataStored);
            resolve();
          })
        );
      })
        .then(() => {
          if (seq !== this.retrieveDataSeq) {
            return;
          }
          this.#store.setStateVisibleOnWindowData(this.currentDataStoreKey);
          // the conflict check reads the record again: not when it was just read from the server (same data)
          if (
            this.editTabs &&
            this.editTabs.length > 0 &&
            this.editTabs[0].data &&
            !this.editTabs[0].data.isView &&
            !this.#store.isFreshFromServer(loaded)
          ) {
            this.subscriptions.push(
              this.#store
                .syncWithRemoteWindowData(
                  this.currentDataStoreKey,
                  true,
                  this,
                  this.#connectorService.getIupicsUserContext()
                )
                .subscribe()
            );
          }
        })
        .then(() => {
          if (seq === this.retrieveDataSeq && environment.constant.mergeLevel !== 0) {
            this.dataMergeSub?.unsubscribe();
            this.dataMergeSub = this.#socketService
              .openDataChannel<any>(this.currentDataStoreKey.windowId + '-' + this.currentDataStoreKey.recordId)
              .subscribe((response) => {
                if (response.from !== this.#socketService.getSocketId()) {
                  this.subscriptions.push(
                    this.#store
                      .syncWithRemoteWindowData(
                        this.currentDataStoreKey,
                        true,
                        this,
                        this.#connectorService.getIupicsUserContext()
                      )
                      .subscribe()
                  );
                }
              });
          }
        });
    }
  }

  /** @param sameRecord the current record reloaded (after a child save): its request count is kept */
  updateEditTabsData(dataStored: DataStore, tabIdToExclude?: number, sameRecord = false): void {
    this.updateSmartButton(dataStored);
    this.#cancelBadges = this.#afterRecordShown(() => {
      this.updateNbFileAttached(dataStored);
      if (!sameRecord) {
        this.updateNbRequest(dataStored);
      }
    }, this.#cancelBadges);

    // const tabsToUpdate = this.editTabs.filter(
    //   (tab, index) => index === 0 || !tabIdToExclude || tabIdToExclude !== tab.tabId
    // );
    for (let i = 0; i < this.editTabs.length; i++) {
      const tab = this.editTabs[i];

      if (tabIdToExclude && tabIdToExclude === tab.tabId) {
        continue;
      }

      if (i > 0) {
        if (tab.DOMChildrenComponent[0]) {
          if (!tab.data.isSingleRow) {
            EditViewUtils.updateEditViewData(tab, dataStored, this.container, this.tabId);
          } else {
            // Si on est en SingleRow (vue Edit) il faut récupérer les données
            if (tab.updateDisplayLogic(dataStored)) {
              if (dataStored.key.tabId !== tab.tabId) {
                let mapfilter = LogicEvaluator.parseParentConstraint(
                  dataStored.data,
                  tab.gridTabFilter,
                  this.#connectorService.getIupicsUserContext()
                );
                if (mapfilter.replace(/=/g, ',') === dataStored.key.recordId.toString()) {
                  mapfilter = undefined;
                }
                const request: DataStoreRequest = {
                  windowId: dataStored.key.windowId,
                  record_id: dataStored.key.recordId.toString(),
                  parent_constraint: mapfilter,
                  compiereRequest: {
                    windowType: CompiereDataGridType.WINDOW,
                    entityId: tab.tabId,
                    startRow: 0,
                    endRow: 1,
                    validation: tab.getTabWhereClause(),
                    windowCtx: tab.getCurrentContext(),
                  },
                };
                const seq = this.retrieveDataSeq;
                this.subscriptions.push(
                  this.#store.getWindowSingleData(request, true).subscribe((data) => {
                    // a previous record's late answer must not fill the tab of the record shown
                    if (seq === this.retrieveDataSeq) tab.updateData(data);
                  })
                );
              }
            }
          }
          // retrieve Record_ID for the component select/order
          if (tab.DOMChildrenComponent[0].data) {
            tab.DOMChildrenComponent[0].data.recordID = dataStored.key.recordId;
          }
        } else if (tab.children && tab.children.length > 0) {
          EditViewUtils.checkDisplayTabCollapse(
            tab,
            tab.getCurrentContext(dataStored),
            tab.data ? tab.data.displayLogic : tab.children[0].data.displayLogic
          );
          tab.updateData(dataStored);
        }
      } else {
        tab.updateData(dataStored);
        this.changingStepper.next(dataStored);
      }
    }
    EditViewUtils.updateOtherEditView(this.container, this.tabId, this.editTabs, dataStored);
  }

  /**
   * change la visibilité de l'onglet et de ses champs en fonction du nouveau datastore
   * @param dataStored
   */
  updateEditTabsVisibility(dataStored: DataStore): void {
    for (const tab of this.editTabs) {
      if (tab.DOMChildrenComponent[0]) {
        tab.updateDisplayLogic(dataStored);
      }
    }
  }

  onRemoveComponent(event: IupicsEvent): void {
    if (event.type === IupicsTypeEvent.removeChildEvent) {
      event.type = IupicsTypeEvent.removeBreadcrumbItem;
      this.DOMParentComponent.onChildUpdate(event);
      // notify parent about the closure of this edit view
      for (const linkedComponent of this.linkedComponents) {
        if (linkedComponent instanceof AbstractDynamicComponent) {
          linkedComponent.deleteLinkedEditView();
        }
      }
      this.notifierLinkedComponent.next({
        type: IupicsTypeEvent.expandEvent,
        item: { data: this.data },
      });
    }
  }

  transmitDataChange(dataStoreKey: DataStoreKey, tabId: number): boolean {
    let find = false;
    let myIndex = -1;
    // on cherche l'index de son parent
    myIndex = this.container.DOMChildrenComponent.findIndex((component) => {
      if ((component.DOMComponent.instance as EditViewUiComponent).currentDataStoreKey) {
        const recordIdSplit = (component.DOMComponent.instance as EditViewUiComponent).currentDataStoreKey.recordId;
        if (recordIdSplit === dataStoreKey.parentId) {
          return component;
        }
      }
    });
    let i = 1;
    while (!find && this.container.DOMChildrenComponent.length > i) {
      if (
        this.container.DOMChildrenComponent[i].DOMComponent.instance instanceof EditViewUiComponent &&
        this.container.DOMChildrenComponent[i].tabId === tabId
      ) {
        (this.container.DOMChildrenComponent[i].DOMComponent.instance as EditViewUiComponent).retrieveData(
          dataStoreKey
        );
        find = true;
      } else if (this.container.DOMChildrenComponent[i].tabId === tabId) {
        myIndex = i;
      }
      i++;
    }
    if (!find && myIndex + 1 < this.container.DOMChildrenComponent.length) {
      (this.container.DOMChildrenComponent[myIndex + 1].DOMComponent.instance as EditViewUiComponent).removeComponent();
    }
    return find;
  }

  // on save: WS
  /**
   * On prépare la vérification des données avant la sauvegarde
   * @param event
   */
  beforeSave(event: Event): Promise<any> {
    event?.stopPropagation();
    return new Promise((resolve, reject) => {
      if (
        this.#callInProgressService.isSubscriptionDone(this.uuid, 'editview.beforesave') &&
        this.#callInProgressService.isSubscriptionDone(this.uuid, 'editview.saveData')
      ) {
        if (this.editTabs.length > 0) {
          const fieldsMandatory = this.editTabs[0].isConstraintMandatoryRespected();
          const fieldsFormat = this.editTabs[0].isVFormatRespected();
          if (fieldsMandatory.length > 0) {
            this.#messageManager.newMessage(
              new IupicsMessage(
                this.#translateService.instant('generic.warning'),
                this.editTabs[0].getMandatoryFieldsMessage(fieldsMandatory),
                'error'
              )
            );
          } else if (fieldsFormat.length > 0) {
            this.#messageManager.newMessage(
              new IupicsMessage(
                this.#translateService.instant('generic.warning'),
                this.#translateService.instant('editView.invalidFormat') + ' [ ' + fieldsFormat + ' ]',
                'error'
              )
            );
          } else {
            if (this.currentDataStoreKey.recordId.split(',').length > 1) {
              this.#callInProgressService.setSubscription(
                this.uuid,
                'editview.beforesave',
                this.#store
                  .syncWithRemoteWindowData(
                    this.currentDataStoreKey,
                    true,
                    this,
                    this.#connectorService.getIupicsUserContext(),
                    this.checkDataBeforeSave.bind(this),
                    resolve
                  )
                  .subscribe()
              );
            } else {
              this.checkDataBeforeSave(resolve);
            }
          }
        } else {
          if (this.currentDataStoreKey.recordId.split(',').length > 1) {
            this.#callInProgressService.setSubscription(
              this.uuid,
              'editview.beforesave',
              this.#store
                .syncWithRemoteWindowData(
                  this.currentDataStoreKey,
                  true,
                  this,
                  this.#connectorService.getIupicsUserContext(),
                  this.checkDataBeforeSave.bind(this),
                  resolve
                )
                .subscribe()
            );
          } else {
            this.checkDataBeforeSave(resolve);
          }
        }
      }
    });
  }

  checkFieldMandatoryAndFormat(): boolean {
    const fieldsMandatory = this.editTabs[0].isConstraintMandatoryRespected();
    const fieldsFormat = this.editTabs[0].isVFormatRespected();
    if (fieldsMandatory.length > 0) {
      this.#messageManager.newMessage(
        new IupicsMessage(
          this.#translateService.instant('generic.warning'),
          this.editTabs[0].getMandatoryFieldsMessage(fieldsMandatory),
          'error'
        )
      );
      return false;
    } else if (fieldsFormat.length > 0) {
      this.#messageManager.newMessage(
        new IupicsMessage(
          this.#translateService.instant('generic.warning'),
          this.#translateService.instant('editView.invalidFormat') + ' [ ' + fieldsFormat + ' ]',
          'error'
        )
      );
      return false;
    }
    return true;
  }
  /**
   * On vérifie les données avant de les sauver
   */
  checkDataBeforeSave(resolve?: (...value: any) => void): void {
    if (!this.conflictsResult || (!this.conflictsResult.hasConflicts && !this.conflictsResult.mustRefresh)) {
      this.saveData(resolve);
    }
  }

  /**
   * On sauve les données en DB et on mets à jour le OLD
   */
  saveData(resolve?: (...value: any) => void): void {
    let status: DataStoreStatus;
    const request: DataStoreRequest = {
      windowId: this.currentDataStoreKey.windowId,
      record_id: this.currentDataStoreKey.recordId,
      parent_constraint: this.currentDataStoreKey.parentId,
      compiereRequest: {
        windowType: CompiereDataGridType.WINDOW,
        entityId: this.currentDataStoreKey.tabId,
        startRow: 0,
        endRow: 1,
        validation: this.getTabWhereclause(),
        windowCtx: this.getCurrentContext(),
      },
    };
    if (this.#callInProgressService.isSubscriptionDone(this.uuid, 'editview.saveData')) {
      this.#callInProgressService.setSubscription(
        this.uuid,
        'editview.saveData',
        this.#store
          .getWindowSingleData(request)
          .pipe(
            switchMap((datastore: DataStore) => {
              status = datastore.status;
              this.isLoading = true;
              if (datastore.calloutStates.size > 0) {
                return datastore.calloutStackEmptied.pipe(
                  switchMap((response) => {
                    return this.#store.saveWindowData(
                      [this.currentDataStoreKey],
                      EditViewUtils.mergeCurrentDataDeepCopy(this.getCurrentContext(), { '#APIZ_MANUAL_SAVE': 'Y' })
                    );
                  })
                );
              } else {
                return this.#store.saveWindowData(
                  [this.currentDataStoreKey],
                  EditViewUtils.mergeCurrentDataDeepCopy(this.getCurrentContext(), { '#APIZ_MANUAL_SAVE': 'Y' })
                );
              }
            })
          )
          .subscribe({
            next: (response: Map<DataStoreKey, DataStore>) => {
              this.isLoading = false;
              if (response) {
                const res = Array.from(response.values())[0];
                const prevKey = { ...this.currentDataStoreKey };
                if (res.key) {
                  // saved record is now the view: a late retrieveData reply must not overwrite it
                  ++this.retrieveDataSeq;
                  this.currentDataStoreKey = res.key;
                }
                if (this.editTabs[0].data.tabLevel === 0) {
                  this.DOMParentComponent.notifyUrlChange(res.data.Data_UUID);
                }
                if (res && res.data) {
                  const gridElement: GridViewUiComponent = this.linkedComponents[0];
                  if (gridElement && this.currentDataStoreKey.recordId && prevKey.recordId.split(',').length === 1) {
                    gridElement.selectedRecordId = this.currentDataStoreKey.recordId;
                  }
                  if (this.isZoomEditView) {
                    this.notifierLinkedComponent.next({
                      refreshZoom: true,
                      id: res.data['Data_UUID'].split(',')[1],
                    });
                  } else {
                    if (status === DataStoreStatus.NEWRECORD) {
                      if (prevKey) {
                        this.#store.deleteDataFromStoreOnly(prevKey);
                      }
                      this.notifierLinkedComponent.next({
                        type: IupicsTypeEvent.REFRESH_GRID,
                      });
                      if (this.data?.tabLevel === 0) {
                        const recentItem_request: DataStoreRequest = {
                          windowId: (this.container as BladeUiComponent).infoComponent.windowId,
                          record_id: res.key.recordId,
                          parent_constraint: this.currentDataStoreKey.parentId,
                          compiereRequest: {
                            windowType: CompiereDataGridType.WINDOW,
                            entityId: this.tabId,
                            startRow: 0,
                            endRow: 1,
                            validation: this.getTabWhereclause(),
                            windowCtx: this.getCurrentContext(),
                          },
                        };
                        this.subscriptions.push(
                          this.#riManager
                            .addRecentItem(
                              recentItem_request,
                              this.editTabs.find((editTab) => editTab.tabId === this.tabId).data.label
                            )
                            .subscribe()
                        );
                      }
                    } else {
                      this.notifierLinkedComponent.next({
                        type: IupicsTypeEvent.UPDATE_ROWSAVED,
                        item: { dataStoreKey: prevKey },
                      });
                    }
                  }
                  this.updateEditTabsData(res);
                  if (!res?.data?.['apiz_dataResult']?.['responseError']) {
                    this.#flashSaved();
                  }
                  if (resolve) {
                    resolve();
                  }
                  if (environment.constant.mergeLevel !== 0) {
                    this.#socketService.broadcast(
                      this.currentDataStoreKey.windowId + '-' + this.currentDataStoreKey.recordId,
                      {
                        from: this.#socketService.getSocketId(),
                        windowId: this.currentDataStoreKey.windowId,
                        recordId: this.currentDataStoreKey.recordId,
                      }
                    );
                  }
                }
              }
              this.#callInProgressService.completeSubscription(this.uuid, 'editview.saveData');
            },
            error: (err) => {
              this.isLoading = false;
              this.#messageManager.newMessage(
                new IupicsMessage(
                  this.#translateService.instant('generic.error'),
                  this.#translateService.instant('editView.saveErrorMessage'),
                  'error'
                )
              );
              this.#callInProgressService.completeSubscription(this.uuid, 'editview.saveData');
            },
          })
      );
    }
  }

  deleteData(event: MouseEvent): void {
    if (!this.checkAndExecuteOverride('delete')) {
      Global.infoDialog.message = {
        summary: this.#translateService.instant('infodialog.dialogs.delete.title'),
        detail: this.#translateService.instant('infodialog.dialogs.delete.message'),
      };
      Global.infoDialog.dialogType = InfoDialogType.CONFIRM_YESNO;
      Global.infoDialog.confirmLabel = this.#translateService.instant('generic.delete');
      Global.infoDialog.cancelLabel = this.#translateService.instant('generic.cancel');
      Global.infoDialog.danger = true;
      Global.infoDialog.showInfoDialog();
      const confirm = Global.infoDialog.confirm.subscribe((e: any) => {
        const dataUUIDs = this.#store.getDataUUIDFromTabID(this.currentDataStoreKey.tabId);
        const recordIds = [];
        const recordId = {};
        for (const dataUUID of dataUUIDs) {
          const data = this.editTabs[0].dataStored.data[dataUUID];
          if (data instanceof Object) {
            recordId[dataUUID] = data.id;
          } else {
            recordId[dataUUID] = data;
          }
        }
        recordIds.push(recordId);
        this.isLoading = true;
        this.subscriptions.push(
          this.#store
            .deleteWindowData(this.currentDataStoreKey, recordIds)
            .pipe(
              switchMap((res) =>
                res.success > 0 && this.data?.tabLevel === 0
                  ? this.#riManager.deleteRecentItems(this.currentDataStoreKey, recordIds).pipe(map(() => res))
                  : of(res)
              )
            )
            .subscribe({
              next: (res) => {
                if (res) {
                  if (this.isZoomEditView) {
                    this.notifierLinkedComponent.next({
                      refreshZoom: true,
                      id: null,
                    });
                  }
                  this.notifierLinkedComponent.next({
                    type: IupicsTypeEvent.REFRESH_GRID,
                  });

                  if ((!res.errors || res.errors.length == 0) && res.success !== 0) {
                    this.removeComponent();
                  }

                  this.#messageManager.newMessage(
                    new IupicsMessage(
                      this.#translateService.instant('editView.deleteMessageTitle'),
                      res.success > 0
                        ? this.#translateService.instant('editView.deleteMessage')
                        : res.errors.length > 0
                          ? res.errors[0].message
                          : this.#translateService.instant('editView.deleteErrorMessage'),
                      res.success > 0 ? 'success' : 'error'
                    )
                  );

                  this.isLoading = false;
                } else {
                  throw new Error(this.#translateService.instant('editView.deleteErrorMessage'));
                }
              },
              error: (error) => {
                this.isLoading = false;
                if (error && error.error) {
                  throw new Error(error.error.message);
                } else {
                  throw new Error(error.message);
                }
              },
            })
        );
        confirm.unsubscribe();
        cancel.unsubscribe();
      });
      const cancel = Global.infoDialog.cancel.subscribe((e) => {
        confirm.unsubscribe();
        cancel.unsubscribe();
      });
    }
    event.stopPropagation();
  }

  /**
   * On refresh OLD dans WS
   * @param event
   */
  refreshData(showMessage = true, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.checkAndExecuteOverride('refresh')) {
      if (this.editTabs[0].dataStored.status !== DataStoreStatus.NEWRECORD) {
        this.updateAll(showMessage);
      } else {
        this.openNew();
      }
    }
  }

  updateAll(showMessage: boolean, tabToExlude = null, callBack?: (...value: any) => void): void {
    if (this.#callInProgressService.isSubscriptionDone(this.uuid, 'editview.updateAll')) {
      this.#callInProgressService.setSubscription(
        this.uuid,
        'editview.updateAll',
        this.#store
          .syncSingleDataWithRemote(
            this.currentDataStoreKey,
            false,
            this,
            this.#connectorService.getIupicsUserContext()
          )
          .subscribe({
            next: (dataStore: DataStore) => {
              this.notifierLinkedComponent.next({
                type: IupicsTypeEvent.UPDATE_ROWSAVED,
                item: { dataStoreKey: this.currentDataStoreKey },
              });
              // a refresh asked by the user (showMessage) reads the request count again, a child save does not
              this.updateEditTabsData(dataStore, tabToExlude, !showMessage);
              this.conflictsResult.mustRefresh = false;
              this.conflictsResult.hasConflicts = false;
              this.conflictsResult.refreshAuto = false;
              if (showMessage) {
                this.#messageManager.newMessage(
                  new IupicsMessage(
                    this.#translateService.instant('editView.refreshMessageTitle'),
                    this.#translateService.instant('editView.refreshMessage'),
                    'success'
                  )
                );
              }
              this.#callInProgressService.completeSubscription(this.uuid, 'editview.updateAll');
              if (callBack) {
                callBack();
              }
            },
            error: ({ error, message }) => {
              this.#messageManager.newMessage(
                new IupicsMessage(
                  this.#translateService.instant('editView.refreshMessageTitle'),
                  message,
                  'error',
                  error
                )
              );
              this.#callInProgressService.completeSubscription(this.uuid, 'editview.updateAll');
              if (callBack) {
                callBack();
              }
            },
          })
      );
    }
  }

  updateCurrSymbol(): void {
    const editViews: EditViewUiComponent[] = this.DOMParentComponent.DOMChildrenComponent.filter(
      (c: any) => c instanceof EditViewUiComponent
    );
    // Flatten: a map() here produced an array of arrays, so the isMoneyField filter never matched
    const dataContainers: AbstractDataContainer[] = editViews
      .filter((editTab) => editTab.tabId !== this.tabId)
      .flatMap((e) => e.editTabs[0]?.dataContainers ?? []);
    // initCurrency reads dataStored.data: skip fields not bound to a record yet
    const moneyFields = dataContainers.filter((dc) => dc.isMoneyField && dc.dataStored?.data);
    for (const moneyField of moneyFields) {
      moneyField.initCurrency(moneyField.dataStored);
    }
  }

  onMerge(mergedData: { dataMerged: object; mergedInfo: object }): void {
    this.wantToMergeConflicts = false;
    this.conflictsResult.hasConflicts = false;
    const request: DataStoreRequest = {
      windowId: this.currentDataStoreKey.windowId,
      record_id: this.currentDataStoreKey.recordId,
      parent_constraint: this.currentDataStoreKey.parentId,
      compiereRequest: {
        windowType: CompiereDataGridType.WINDOW,
        entityId: this.currentDataStoreKey.tabId,
        startRow: 0,
        endRow: 1,
        validation: this.getTabWhereclause(),
        windowCtx: this.getCurrentContext(),
      },
    };
    this.subscriptions.push(
      this.#store.getWindowSingleData(request).subscribe((data) => {
        new Promise((resolve) => {
          this.#store.syncDataChanges(data, mergedData.dataMerged, true);
          this.#store.copyWindowDataToOldStore(data);
          const columnNames = Object.keys(mergedData.mergedInfo);
          for (const columnName of columnNames) {
            if (mergedData.mergedInfo[columnName].who === 'local') {
              this.#store.copyRemoteWindowDataToOldStore(data.key, columnName);
            }
          }
          resolve(undefined);
        }).then(() => {
          this.beforeSave(document.createEvent('MouseEvent'));
        });
      })
    );
  }

  /**
   * Lance l'impression du report et gestion du retour
   * @param event
   */
  printReport(event: MouseEvent): void {
    if (this.#callInProgressService.isSubscriptionDone(this.uuid, 'editview.printReport')) {
      this.#callInProgressService.setSubscription(
        this.uuid,
        'editview.printReport',
        this.#printReportManager.printAndDownloadReport(
          this.currentDataStoreKey,
          this.#connectorService.getIupicsUserAccount().session_id,
          this.#store.getStore(this.editTabs[0].dataStoreKey, DataStoreName.CURRENT).data,
          () => {
            this.updateNbFileAttached();
          }
        )
      );
    }
    event.stopPropagation();
  }

  /**
   * On déplace la vue au point d'ancrage
   * @param editTab
   */
  //#region section bar and menus of the info bar
  /** open menu of the info bar (click, not hover) */
  menuOpen: 'process' | null = null;
  /** section at the top of the scrolled form */
  activeSectionKey: string | null = null;
  #unlistenSectionScroll: (() => void) | undefined;

  /**
   * processes of this record (menu "Processes"): the buttons of the top tab and of its field groups. The ids are
   * compared as text and a process registered without tab id (field group) is kept: the strict comparison on the
   * view's tab id left the menu empty.
   */
  tabProcesses() {
    const ids = new Set([this.tabId, this.editTabs[0]?.tabId].filter((id) => id !== undefined && id !== null).map(String));
    return this.processes().filter((p) => p.tabId === undefined || p.tabId === null || ids.has(String(p.tabId)));
  }

  toggleMenu(menu: 'process', event: Event): void {
    event.stopPropagation();
    this.menuOpen = this.menuOpen === menu ? null : menu;
  }

  closeMenu(focusButton = false): void {
    if (!this.menuOpen) return;
    const menu = this.menuOpen;
    this.menuOpen = null;
    if (focusButton) {
      setTimeout(() => (this.editViewElement?.nativeElement as HTMLElement | undefined)?.querySelector<HTMLElement>(`[data-menu="${menu}"]`)?.focus());
    }
  }

  // a click elsewhere closes the Processes / Links menu (outside Angular: no app check on every click)
  readonly #documentClickMenus = listenOutsideAngular(
    'click',
    () => !!this.menuOpen,
    () => (this.menuOpen = null)
  );

  /** sections of the form, in page order: the record, its field groups, the child tabs */
  sectionItems(): EditViewSection[] {
    const top = this.editTabs[0];
    if (!top) return [];
    const items: EditViewSection[] = [{ key: 'top', label: top.label ?? top.data?.label ?? '', todo: false, kind: 'top', comp: top }];
    for (const child of top.DOMChildrenComponent ?? []) {
      if (child instanceof AccordionUiComponent && child.displayCss !== 'none' && child.label) {
        // a group with an empty mandatory field: shown on its chip (also when the group is closed)
        items.push({ key: 'group-' + child.uuid, label: child.label, todo: !!child.isMandatory, kind: 'group', comp: child });
      }
    }
    for (const tab of this.editTabs.slice(1)) {
      if (tab.displayCss !== 'none' && tab.label) {
        items.push({ key: 'tab-' + tab.tabId, label: tab.label, todo: false, kind: 'tab', comp: tab });
      }
    }
    return items;
  }

  /** while a chip click scrolls the form, the scroll spy keeps the clicked section (no jump to another chip) */
  #spyLockedUntil = 0;
  #navFrame = 0;

  goToSection(section: EditViewSection, event: Event): void {
    event.stopPropagation();
    this.activeSectionKey = section.key;
    this.#spyLockedUntil = performance.now() + 2000;
    this.#revealActiveSection();
    if (section.kind === 'top') {
      this.#scrollFormTo(0);
      return;
    }
    if (section.kind === 'tab') {
      const tab = section.comp as EditTabUiComponent;
      // a grid built on opening scrolls itself once sized (goToAnchor / isWaitingScroll)
      if (!tab.childrenCreated && tab.children?.[0]?.component === 'GridViewUiComponent') {
        this.goToAnchor(event as MouseEvent, tab);
        return;
      }
      if (tab.collapsed) tab.collapsed = false;
    } else {
      (section.comp as AccordionUiComponent).expand();
    }
    this.#scrollWhenStable(section.comp.elementRef?.nativeElement as HTMLElement | undefined);
  }

  /**
   * Scrolls to a section once it is open: the position read before the opening animation ended was wrong (the
   * form, still short, was scrolled to its end first). Waits until the section and the form keep the same height
   * for a few frames (at most 800 ms), then scrolls once.
   */
  #scrollWhenStable(el: HTMLElement | undefined): void {
    const scroller = this.scrollPanel?.contentViewChild?.nativeElement as HTMLElement | undefined;
    if (!scroller || !el) return;
    cancelAnimationFrame(this.#navFrame);
    const start = performance.now();
    let lastHeight = -1;
    let lastScrollHeight = -1;
    let stableFrames = 0;
    this.#zone.runOutsideAngular(() => {
      const step = () => {
        const height = el.offsetHeight;
        const scrollHeight = scroller.scrollHeight;
        stableFrames = height === lastHeight && scrollHeight === lastScrollHeight ? stableFrames + 1 : 0;
        lastHeight = height;
        lastScrollHeight = scrollHeight;
        if (stableFrames >= 3 || performance.now() - start > 800) {
          const y = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - 4;
          this.#scrollFormTo(Math.max(0, y));
          this.#flashSection(el);
          return;
        }
        this.#navFrame = requestAnimationFrame(step);
      };
      this.#navFrame = requestAnimationFrame(step);
    });
  }

  #flashSaved(): void {
    this.justSaved = true;
    clearTimeout(this.#justSavedTimer);
    this.#justSavedTimer = setTimeout(() => (this.justSaved = false), 1400);
  }

  /** the section reached from the section bar is outlined a moment (class only, outside Angular) */
  #flashSection(el: HTMLElement | undefined): void {
    if (!el) return;
    el.classList.remove('iu-flash');
    void el.offsetWidth; // restart the animation on a second click
    el.classList.add('iu-flash');
    this.#zone.runOutsideAngular(() => setTimeout(() => el.classList.remove('iu-flash'), 900));
  }

  #scrollFormTo(y: number): void {
    this.scrollPanel?.scrollTop(y);
    // the scroll events of this move belong to the click
    this.#spyLockedUntil = performance.now() + 300;
  }

  /** the section bar follows the scroll (outside Angular, one check per frame) */
  #listenSectionScroll(): void {
    const scroller = this.scrollPanel?.contentViewChild?.nativeElement as HTMLElement | undefined;
    if (!scroller) return;
    this.#zone.runOutsideAngular(() => {
      let frame = 0;
      const onScroll = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.#spySection(scroller));
      };
      scroller.addEventListener('scroll', onScroll, { passive: true });
      this.#unlistenSectionScroll = () => {
        cancelAnimationFrame(frame);
        scroller.removeEventListener('scroll', onScroll);
      };
    });
  }

  #spySection(scroller: HTMLElement): void {
    if (performance.now() < this.#spyLockedUntil) return;
    const items = this.sectionItems();
    if (items.length < 2) return;
    let current = items[0].key;
    // reading line: near the top of the form, moved down over the last screen of scroll so that short last
    // sections become current one after the other (jumping to the last one at the bottom skipped them)
    const box = scroller.getBoundingClientRect();
    const maxScroll = scroller.scrollHeight - scroller.clientHeight;
    const remaining = Math.max(0, maxScroll - scroller.scrollTop);
    const span = Math.min(scroller.clientHeight, maxScroll);
    const progress = span > 0 && remaining < span ? 1 - remaining / span : 0;
    const line = box.top + 24 + progress * Math.max(0, scroller.clientHeight - 48);
    if (scroller.scrollTop > 2) {
      for (const s of items.slice(1)) {
        const el = s.comp.elementRef?.nativeElement as HTMLElement | undefined;
        if (el && el.getClientRects().length && el.getBoundingClientRect().top <= line) current = s.key;
      }
    }
    if (current !== this.activeSectionKey) {
      this.#zone.run(() => (this.activeSectionKey = current));
      this.#revealActiveSection();
    }
  }

  //#region section bar overflow: fades and arrows on the cut side, current chip kept in view, wheel scrolls sideways
  @ViewChild('sectionWrap') sectionWrap: ElementRef<HTMLElement>;
  #sectionNavCleanup: (() => void) | undefined;

  /** the bar is inside an @if: its listeners follow it (outside Angular, classes set on the DOM) */
  @ViewChild('sectionNav') set sectionNavRef(ref: ElementRef<HTMLElement> | undefined) {
    this.#sectionNavCleanup?.();
    this.#sectionNavCleanup = undefined;
    const nav = ref?.nativeElement;
    if (!nav) return;
    this.#zone.runOutsideAngular(() => {
      const update = () => this.#updateSectionsOverflow(nav);
      const wheel = (e: WheelEvent) => {
        // vertical wheel over the bar scrolls it sideways (when it overflows)
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && nav.scrollWidth > nav.clientWidth) {
          nav.scrollLeft += e.deltaY;
          e.preventDefault();
        }
      };
      nav.addEventListener('scroll', update, { passive: true });
      nav.addEventListener('wheel', wheel, { passive: false });
      const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : undefined;
      observer?.observe(nav);
      requestAnimationFrame(() => {
        update();
        this.#revealActiveSection(false);
      });
      this.#sectionNavCleanup = () => {
        nav.removeEventListener('scroll', update);
        nav.removeEventListener('wheel', wheel);
        observer?.disconnect();
      };
    });
  }

  #updateSectionsOverflow(nav: HTMLElement): void {
    const wrap = this.sectionWrap?.nativeElement;
    if (!wrap) return;
    wrap.classList.toggle('ev-sections-wrap--left', nav.scrollLeft > 2);
    wrap.classList.toggle('ev-sections-wrap--right', nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 2);
  }

  scrollSections(direction: 1 | -1): void {
    const nav = this.sectionWrap?.nativeElement.querySelector<HTMLElement>('.ev-sections');
    nav?.scrollBy({ left: direction * Math.max(120, nav.clientWidth * 0.7), behavior: 'smooth' });
  }

  /** the current chip fully visible in the bar (not cut under a fade or an arrow) */
  #revealActiveSection(smooth = true): void {
    const wrap = this.sectionWrap?.nativeElement;
    const nav = wrap?.querySelector<HTMLElement>('.ev-sections');
    const key = this.activeSectionKey;
    const chip = (key ? nav?.querySelector<HTMLElement>(`[data-key="${key}"]`) : null) ?? nav?.querySelector<HTMLElement>('.ev-section');
    if (!nav || !chip) return;
    const margin = 32;
    const left = chip.offsetLeft;
    const right = left + chip.offsetWidth;
    let target: number | null = null;
    if (left - margin < nav.scrollLeft) target = Math.max(0, left - margin);
    else if (right + margin > nav.scrollLeft + nav.clientWidth) target = right + margin - nav.clientWidth;
    if (target !== null) nav.scrollTo({ left: target, behavior: smooth ? 'smooth' : 'auto' });
  }
  //#endregion

  //#region info bar on two lines
  @ViewChild('infoBar', { static: true }) infoBar: ElementRef<HTMLElement>;
  #infoBarObserver: ResizeObserver | undefined;

  /**
   * When the status group does not fit next to the processes and links, it goes to a second line of the bar: the
   * class then lays it out as a row of its own (full width, divider, status on the left) instead of a block
   * floating on the right under an empty space. Measured without the class (outside Angular, on resize).
   */
  #watchInfoBarWrap(): void {
    const bar = this.infoBar?.nativeElement;
    if (!bar || typeof ResizeObserver === 'undefined') return;
    this.#zone.runOutsideAngular(() => {
      let frame = 0;
      const check = () => {
        const right = bar.querySelector<HTMLElement>(':scope > .ev-right');
        const first = bar.firstElementChild as HTMLElement | null;
        bar.classList.remove('ev-info--wrapped');
        if (!right || !first || right === first || bar.clientWidth === 0) return;
        if (right.offsetTop > first.offsetTop + 4) bar.classList.add('ev-info--wrapped');
      };
      this.#infoBarObserver = new ResizeObserver(() => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(check);
      });
      this.#infoBarObserver.observe(bar);
    });
  }
  //#endregion

  /** "Additional information" panel, opened from the info bar */
  toggleAdditional(): void {
    if (this.isAdditionalOpened) this.minimize();
    else this.resize();
  }
  //#endregion

  goToAnchor(event: MouseEvent, editTab: EditTabUiComponent): void {
    event.stopPropagation();
    const shouldWaitResizeBeforeScroll =
      !editTab.childrenCreated && editTab.children[0] && editTab.children[0].component === 'GridViewUiComponent';
    if (editTab.collapsed) {
      editTab.collapsed = false;
    }
    if (shouldWaitResizeBeforeScroll) {
      this.isWaitingScroll = true;
    } else {
      const elem = editTab?.elementRef.nativeElement;
      const topPos = elem.offsetTop;
      this.scrollPanel.scrollTop(topPos);
    }
  }

  ngOnDestroy() {
    clearTimeout(this.#justSavedTimer);
    this.#cancelBadges?.();
    this.#cancelPreview?.();
    super.ngOnDestroy();
    this.#unlistenSectionScroll?.();
    this.#sectionNavCleanup?.();
    this.#infoBarObserver?.disconnect();
    cancelAnimationFrame(this.#navFrame);
    this.removeResizeListeners();
    if (this.sideComponentRef) this.sideComponentRef.destroy();
    if (environment.constant.mergeLevel !== 0) {
      if (this.currentDataStoreKey) {
        this.#socketService.closeDataChannel(
          this.currentDataStoreKey.windowId + '-' + this.currentDataStoreKey.recordId
        );
      }
    }
    this.vcrAudit?.clear();
    this.vcrAudit = undefined;
    this.vcrSpecific?.clear();
    this.vcrSpecific = undefined;
    this.sideBarVcr?.clear();
    this.sideBarVcr = undefined;
    this.sideBladeVcr?.clear();
    this.sideBladeVcr = undefined;
    this.editTabs = [];
    this.dataMergeSub?.unsubscribe();
  }

  changeGridViewVisibility(): void {
    this.GridViewVisibility = 'visible';
    this.gridViewUi.GridTabInfinityScrollUiComponent.onlyOneColumn();
    if (this.gridViewUi.GridTabInfinityScrollUiComponent.grid.api.getSelectedNodes().length === 0) {
      // si Aucune ligne n'est sélectionné, on sélectionne la première par défault()
      this.gridViewUi.GridTabInfinityScrollUiComponent.grid.api.getRowAtIndex(0).setSelected(true);
    }
    // cache la checkbox
    this.gridViewUi.GridTabInfinityScrollUiComponent.grid.columnApi.setColumnVisible('Data_UUID', false);
  }

  listenSmartButtonClick(): void {
    // Smart buttons used to nest the target's EditViewUiComponent inside this window's own
    // container (component: DOMParentComponent = this.container), which renders "beside" the
    // current record instead of opening a real new window/tab. We now resolve the same
    // FK-filtered related record (same parent_constraint lookup as before) and open it through
    // WorkspaceService, which creates a genuinely separate tab - keeping the filter that pointed
    // to that related record instead of losing it.
    this.#smartButtonManagerService.click.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (button) => {
        // opens a separate tab: close the toggle, else the button stays hidden and re-opens on each record change
        this.#smartButtonManagerService.registration.updateData((buttons) =>
          buttons.map((b) => (b.key === button.key ? { ...b, isOpened: false } : b))
        );
        this.#uiCreatorService.getWindow(button.key).subscribe((tabUI) => {
          const parentConstraint = this.currentDataStoreKey.recordId.replace(/,/g, '=');
          const request: DataStoreRequest = {
            windowId: button.key,
            parent_constraint: parentConstraint,
            compiereRequest: {
              windowType: CompiereDataGridType.WINDOW,
              entityId: tabUI.tabId,
              startRow: 0,
              endRow: 1,
              validation: '',
              windowCtx: this.getCurrentContext(),
            },
          };

          this.subscriptions.push(
            this.#store.getWindowSingleData(request).subscribe((dataStore: DataStore) => {
              if (!dataStore) {
                this.#messageManager.newMessage(
                  new IupicsMessage(
                    this.#translateService.instant('generic.warning'),
                    this.#translateService.instant('notification.zoomInfoNotValid'),
                    'error'
                  )
                );
                return;
              }
              const dataUUID = dataStore.data['Data_UUID'];
              this.#workspaceService.openTargetSearchEmt.emit({
                zoomInfo: {
                  windowId: button.key,
                  dataUUID,
                  record_id: dataUUID,
                  children: null,
                  isInCurrentWindow: false,
                },
                cat: { id: button.key },
                source: { id: dataUUID },
              });
            })
          );
        });
      },
    });
  }

  gridViewCellClicked($event: any): void {
    const request: DataStoreRequest = {
      windowId: (this.container as BladeUiComponent).infoComponent.windowId,
      record_id: $event.recordId,
      parent_constraint: this.zoomTarget.record_id.replace(/,/g, '='),
      compiereRequest: {
        windowType: CompiereDataGridType.WINDOW,
        entityId: this.tabId,
        startRow: 0,
        endRow: 1,
        validation: this.getTabWhereclause(),
        windowCtx: this.getCurrentContext(),
      },
    };
    // view switches record: invalidate any in-flight load now, and ignore this reply if a newer one started
    const seq = ++this.retrieveDataSeq;
    this.subscriptions.push(
      this.#store.getWindowSingleData(request).subscribe((dataStore) => {
        if (seq !== this.retrieveDataSeq) {
          return;
        }
        this.currentDataStoreKey = this.#store.generateDataStoreKey(
          (this.container as BladeUiComponent).infoComponent.windowId,
          this.tabId,
          dataStore.data['Data_UUID'],
          this.zoomTarget.record_id.replace(/,/g, '=')
        );
        this.updateEditTabsData(dataStore);
        this.GridViewVisibility = 'hidden';
      })
    );
  }

  openExportDataModal(event: Event): void {
    event.stopPropagation();

    if (!this.checkAndExecuteOverride('export')) {
      this.displayExportDataModal = true;
    }
  }

  viewRecordChangeLog(): void {
    const formID = this.#config.getConstant('ChangeLogWindowUIComponent#FormID');
    this.showAuditsPanel = true;

    setTimeout(() => {
      this.createSpecificWindow(formID, undefined, this.vcrAudit, () => {
        this.showAuditsPanel = false;
      });
    }, 5);
  }

  openNew(event?: Event): void {
    event?.stopPropagation();

    if (this.checkAndExecuteOverride('new')) {
      return;
    }

    let i = 1;
    let find = false;
    while (i < this.container.DOMChildrenComponent.length && !find) {
      if (this.container.DOMChildrenComponent[i] === this && ++i < this.container.DOMChildrenComponent.length) {
        (this.container.DOMChildrenComponent[i] as EditViewUiComponent).removeComponent();
        find = true;
      } else {
        i++;
      }
    }
    const gridElement = this.linkedComponents[0] as GridViewUiComponent;
    if (gridElement?.checkEditViewCreated?.()) {
      const parentComp = gridElement.getFirstEditViewParent(gridElement.DOMParentComponent);
      if (parentComp) {
        parentComp.gridTabIdSelected = this.tabId;
      }
    }
    if (this.checkData()) {
      // check si c'est l'edit view de premier niveau
      if (this.currentDataStoreKey.parentId === '') {
        this.DOMParentComponent.notifyUrlChange('newRecord');
      }
      const key = this.#store.newWindowData(
        this.container.infoComponent.windowId,
        this.tabId,
        null,
        EditViewUtils.getParentDatastoreKey(this)
      ).key;
      this.retrieveData(key);
    } else {
      const ctx = this.#connectorService.getIupicsUserContext();
      if (ctx['AutoCommit'] === 'Y') {
        this.beforeSave(document.createEvent('Event')).then(() => {
          if (
            this.conflictsResult &&
            this.conflictsResult.hasConflicts === false &&
            this.conflictsResult.mustRefresh === false
          ) {
            const key = this.#store.newWindowData(
              this.container.infoComponent.windowId,
              this.tabId,
              null,
              EditViewUtils.getParentDatastoreKey(this)
            ).key;
            this.retrieveData(key);
          }
        });
      } else {
        const openNewRecord = () => {
          const key = this.#store.newWindowData(
            this.container.infoComponent.windowId,
            this.tabId,
            null,
            EditViewUtils.getParentDatastoreKey(this)
          ).key;
          this.retrieveData(key);
        };
        this.#askUnsavedChanges(
          'change',
          () =>
            this.beforeSave(document.createEvent('Event')).then(() => {
              if (
                this.conflictsResult &&
                this.conflictsResult.hasConflicts === false &&
                this.conflictsResult.mustRefresh === false
              ) {
                openNewRecord();
              }
            }),
          () => {
            if (this.editTabs[0].dataStored.status !== DataStoreStatus.NEWRECORD) {
              this.subscriptions.push(this.#store.syncWithRemoteWindowData(this.currentDataStoreKey).subscribe());
            } else {
              this.#store.deleteDataFromStoreOnly(this.currentDataStoreKey);
            }
            openNewRecord();
          }
        );
      }
    }
  }

  /*
   * emailEditor: { display: { key: 'displayEmailEditor', value: displayEmailEditor }}
   * joinFilesPanel: { display: { key: 'displayJoinFilesPanel', value: displayJoinFilesPanel }}
   * processModal: { display: { key: 'displayProcessUI', value: displayProcessUI }, id: { key: 'processId', value: processId } }
   * formModal: { display: { key: 'displayFormUI', value: displayFormUI }, id: { key: 'formId', value: formId } }
   */
  updateModalDisplay({ display, id }: ModalDisplayRequest): void {
    if (display?.sourceComponent?.columnName) {
      this.modalClass = 'p-col-10 p-md-6 p-lg-4';
    } else {
      this.modalClass = 'p-col-10 p-md-10 p-lg-10';
    }
    this.customFormModalBodyCss =
      display.key && display.key === 'displayFormUI'
        ? { 'iu-modal-body': { overflow: 'auto', height: 'calc(100% - 25px)' } }
        : undefined;
    const isButton = display?.sourceComponent?.itemData?.component === 'ButtonUiComponent';
    if (isButton && !this.#store.checkDataBeforeNewLine(this.currentDataStoreKey)) {
      this.beforeSave(null).then((result) => {
        this.displayModal(display, id);
      });
    } else {
      this.displayModal(display, id);
    }
    if (!display.value && this.isProcessLaunch) {
      this.refreshEditViews(true); // 132675
      this.isProcessLaunch = false;
    }
  }

  updateModalDisplayByValue(
    display: { key: string; value: boolean; sourceComponent?: any },
    id?: { key: string; value: string }
  ) {
    if (display?.sourceComponent?.columnName) {
      this.modalClass = 'p-col-10 p-md-6 p-lg-4';
    } else {
      this.modalClass = 'p-col-10 p-md-10 p-lg-10';
    }
    this.customFormModalBodyCss =
      display.key && display.key === 'displayFormUI'
        ? { 'iu-modal-body': { overflow: 'auto', height: 'calc(100% - 25px)' } }
        : undefined;
    const isButton = display?.sourceComponent?.itemData?.component === 'ButtonUiComponent';
    if (isButton && !this.#store.checkDataBeforeNewLine(this.currentDataStoreKey)) {
      this.beforeSave(null).then((result) => {
        this.displayModalbyValue(display, id);
      });
    } else {
      this.displayModalbyValue(display, id);
    }
    if (!display.value && this.isProcessLaunch) {
      this.refreshEditViews(true); // 132675
      this.isProcessLaunch = false;
    }
  }

  displayModalbyValue(
    display: { key: string; value: boolean; sourceComponent?: any },
    id?: { key: string; value: string }
  ) {
    this[display.key] = false;
    this[`${display.key}_sourceComponentData`] = display.sourceComponent;
    // Récupération du table_id pour les fichiers joints
    if (this.editTabs.length > 0) {
      this.table_id = this.editTabs[0].data.AD_Table_ID;
    }
    if (id && display.value === true) {
      this[id.key] = id.value;
      if (id.key !== 'processId') {
        this.createSpecificWindowbyValue(id.value, display.sourceComponent, this.vcrSpecific, null, () => {
          this[display.key] = display.value;
        });
      } else {
        this[display.key] = display.value;
      }
    } else {
      this[display.key] = display.value;
      this.customFormModalBodyCss = undefined;
    }
  }

  displayModal(display: { key: string; value: boolean; sourceComponent?: any }, id?: { key: string; value: number }) {
    this[display.key] = false;
    this[`${display.key}_sourceComponentData`] = display.sourceComponent;
    // Récupération du table_id pour les fichiers joints
    if (this.editTabs.length > 0) {
      this.table_id = this.editTabs[0].data.AD_Table_ID;
    }
    if (id && display.value === true) {
      this[id.key] = id.value;
      if (id.key !== 'processId') {
        this.createSpecificWindow(id.value, display.sourceComponent, this.vcrSpecific, null, () => {
          this[display.key] = display.value;
        });
      } else {
        this[display.key] = display.value;
      }
    } else {
      this[display.key] = display.value;
      this.customFormModalBodyCss = undefined;
    }
  }

  /**
   * Copie les data de l'enregistrement courant, les vérifie et les enregistre dans le datastore
   * @param {Event}event
   */
  copyData(event: Event) {
    if (!this.checkAndExecuteOverride('copy')) {
      let i = 1;
      let find = false;
      while (i < this.container.DOMChildrenComponent.length && !find) {
        if (this.container.DOMChildrenComponent[i] === this && ++i < this.container.DOMChildrenComponent.length) {
          (this.container.DOMChildrenComponent[i] as EditViewUiComponent).removeComponent();
          find = true;
        } else {
          i++;
        }
      }

      event.stopPropagation();
      let dataStore = this.#store.newWindowData(
        this.container.infoComponent.windowId,
        this.tabId,
        null,
        EditViewUtils.getParentDatastoreKey(this)
      );

      const request: DataStoreRequest = {
        windowId: this.currentDataStoreKey.windowId,
        record_id: this.currentDataStoreKey.recordId,
        parent_constraint: this.currentDataStoreKey.parentId,
        compiereRequest: {
          windowType: CompiereDataGridType.WINDOW,
          entityId: this.currentDataStoreKey.tabId,
          startRow: 0,
          endRow: 1,
          validation: this.getTabWhereclause(),
          windowCtx: this.getCurrentContext(),
        },
      };

      this.subscriptions.push(
        this.#store.getWindowSingleData(request).subscribe((dataStoreSource: DataStore) => {
          /*pour récupérer les champs invisible dans le store */
          // dataStore is a fresh newWindowData (defaults only): merge the source values of the tab fields
          // (checkForCopy then keeps only the IsCopy ones) and Data_UUID used below. Columns without a
          // field carry no IsCopy flag, so they are not copied (they keep the new record defaults).
          if (dataStoreSource && dataStoreSource.data) {
            const fieldColumns = new Set(this.editTabs[0].dataContainers.map((dc) => dc.data?.columnName));
            fieldColumns.add('Data_UUID');
            for (const columnName of Object.keys(dataStoreSource.data)) {
              if (fieldColumns.has(columnName)) {
                dataStore.data[columnName] = dataStoreSource.data[columnName];
              }
            }
          }
          const isOneKeyCol = dataStore.data.Data_UUID.split(',').length === 2;
          dataStore.data.Data_UUID = dataStore.data.Data_UUID.split(',')[0] + ',' + dataStore.key.recordId;
          dataStore = this.editTabs[0].checkForCopy(dataStore);
          if (isOneKeyCol) {
            dataStore.data[dataStore.data.Data_UUID.split(',')[0]] = null;
          }
          dataStore.isCopied = true;
          // the copy is now the view: a late retrieveData reply must not overwrite it
          ++this.retrieveDataSeq;
          this.updateEditTabsData(dataStore);
          this.currentDataStoreKey = dataStore.key;
          this.DOMParentComponent.notifyUrlChange(dataStore.data.Data_UUID);
        })
      );
    }
  }

  createSpecificWindow(
    formId: number,
    sourceComponent?: any,
    vcr = this.vcrSpecific,
    closeModal?: (...value: any) => void,
    afterLoaded?: (...value: any) => void
  ) {
    this.subscriptions.push(
      this.#uiCreatorService.getSpecificWindow(formId).subscribe((specificWindow) => {
        if (afterLoaded) {
          afterLoaded();
        }

        vcr.clear();
        const componentRef = createComponent<SpecificWindowUiComponent>(
          vcr,
          specificWindow?.angularClass?.length > 0 && specificWindow.angularClass !== 'default'
            ? specificWindow.angularClass
            : 'default'
        );
        this.specificWindowTitle = specificWindow.name;
        componentRef.instance.name = specificWindow.name;
        componentRef.instance.title = specificWindow.title;
        componentRef.instance.description = specificWindow.description;
        componentRef.instance.help = specificWindow.help;
        componentRef.instance.componentRef = componentRef;
        componentRef.instance.isModal = true;
        componentRef.instance.formId = formId;
        componentRef.instance.vcrwindow = vcr;
        componentRef.instance.parentComponent = this;
        componentRef.instance.index = vcr.length - 1;
        componentRef.instance.sourceModal = this.specificModal;
        componentRef.instance.sourceComponentData = sourceComponent?.itemData ?? sourceComponent;
        componentRef.instance.sourceComponent = sourceComponent;
        /**dupliqué si pas d'itemdata mais trop de refactoring sur scout */
        componentRef.instance.closeModalEmitter
          .pipe(takeUntilDestroyed(componentRef.instance.destroyRef))
          .subscribe(() => {
            if (closeModal) {
              closeModal();
            } else {
              this.updateModalDisplay(ModalDisplayRequest.Form(false, null, this));
            }
          });

        this.componentRefs.push(componentRef);
      })
    );
  }

  createSpecificWindowbyValue(
    formId: string,
    sourceComponent?: any,
    vcr = this.vcrSpecific,
    closeModal?: (...value: any) => void,
    afterLoaded?: (...value: any) => void
  ) {
    this.subscriptions.push(
      this.#uiCreatorService.getSpecificWindow(formId).subscribe((specificWindow) => {
        if (afterLoaded) {
          afterLoaded();
        }

        vcr.clear();
        const componentRef = createComponent<SpecificWindowUiComponent>(
          vcr,
          specificWindow?.angularClass?.length > 0 && specificWindow.angularClass !== 'default'
            ? specificWindow.angularClass
            : 'default'
        );
        this.specificWindowTitle = specificWindow.name;
        componentRef.instance.name = specificWindow.name;
        componentRef.instance.title = specificWindow.title;
        componentRef.instance.description = specificWindow.description;
        componentRef.instance.help = specificWindow.help;
        componentRef.instance.componentRef = componentRef;
        componentRef.instance.isModal = true;
        componentRef.instance.formId = specificWindow.AD_Form_ID;
        componentRef.instance.vcrwindow = vcr;
        componentRef.instance.parentComponent = this;
        componentRef.instance.index = vcr.length - 1;
        componentRef.instance.sourceModal = this.specificModal;
        componentRef.instance.sourceComponentData = sourceComponent?.itemData ?? sourceComponent;
        componentRef.instance.sourceComponent = sourceComponent;
        /**dupliqué si pas d'itemdata mais trop de refactoring sur scout */
        componentRef.instance.closeModalEmitter
          .pipe(takeUntilDestroyed(componentRef.instance.destroyRef))
          .subscribe(() => {
            if (closeModal) {
              closeModal();
            } else {
              this.updateModalDisplay(ModalDisplayRequest.Form(false, null, this));
            }
          });

        this.componentRefs.push(componentRef);
      })
    );
  }

  /**
   * permet de passer à la ligne suivante ou précedente
   * @param step avancement ou recul à faire
   */
  changeGridElement(step: number): void {
    if (this.linkedComponents[0] instanceof GridViewUiComponent) {
      const gridElement = this.linkedComponents[0];
      this.simulateClickOnCellUsingArrow(gridElement, step);
    }
  }

  /**
   * simule un clic sur la ligne précedant ou suivant la ligne courante
   * @param gridElement grid sur laquelle on simule un clic
   * @param step avancement ou recul à faire
   */
  simulateClickOnCellUsingArrow(gridElement: GridViewUiComponent, step: number): void {
    const api = gridElement.GridTabInfinityScrollUiComponent.grid.api;
    const currentNodeSelected = api.getSelectedNodes()[0];

    let targetRowIndex: number;

    if (currentNodeSelected) {
      targetRowIndex = +currentNodeSelected.rowIndex + step;
    } else if (gridElement.GridTabInfinityScrollUiComponent.currentSelectIndex >= 0) {
      // No row currently selected, use last selected index
      targetRowIndex = gridElement.GridTabInfinityScrollUiComponent.currentSelectIndex + step;
    } else {
      // No selection and no history, cannot navigate
      return;
    }

    const nbRowPerPage = api.getPageSize();
    const pageId = Math.trunc(targetRowIndex / nbRowPerPage);

    // Set data for the target row
    gridElement.GridTabInfinityScrollUiComponent.currentSelectIndex = targetRowIndex;
    gridElement.GridTabInfinityScrollUiComponent.currentSelectPageIndex = pageId;

    const nodeToSelect = api.getRowAtIndex(targetRowIndex);
    if (nodeToSelect) {
      api.deselectAll();
      api.goToPage(pageId);
      const cell: Partial<IupicsCellEvent> = {
        colDef: { field: 'name' },
        data: nodeToSelect.getRowData(),
        row: nodeToSelect,
        origin: 'edit',
      };

      gridElement.GridTabInfinityScrollUiComponent.onCellClicked({ type: ApizEventType.cellClicked, data: cell });
    } else {
      // Simulation du clic après que le loading soit fini
      setTimeout(() => {
        const loadedNode = api.getRowAtIndex(targetRowIndex);
        if (loadedNode) {
          api.deselectAll();
          api.goToPage(pageId);
          const cell: Partial<IupicsCellEvent> = {
            colDef: { field: 'name' },
            data: loadedNode.getRowData(),
            row: loadedNode,
            origin: 'edit',
          };
          gridElement.GridTabInfinityScrollUiComponent.onCellClicked({ type: ApizEventType.cellClicked, data: cell });
        }
      }, 500);
    }
  }

  generateZoom(): void {
    this.isZoomEditView = true;
    let newData;
    let sub = null;
    // view switches to the zoomed record: invalidate any in-flight retrieveData reply
    ++this.retrieveDataSeq;
    if (this.zoomInfo.record_id === -1) {
      newData = this.#store.newWindowData(this.container.infoComponent.windowId, this.tabId);
      this.currentDataStoreKey = newData.key;
      sub = this.#store.newRecord(this.currentDataStoreKey, this.getCurrentContext());
    } else {
      this.zoomInfo.dataUUID =
        this.zoomInfo.dataUUID && this.zoomInfo.dataUUID.split('.').length > 1
          ? this.zoomInfo.dataUUID.split('.')[1]
          : this.zoomInfo.dataUUID;
      this.currentDataStoreKey = this.#store.generateDataStoreKey(
        this.zoomInfo.windowId,
        this.tabId,
        this.zoomInfo.dataUUID,
        this.zoomInfo.parentId ? this.zoomInfo.parentId : null
      );
      const request: DataStoreRequest = {
        windowId: this.zoomInfo.windowId,
        record_id: this.zoomInfo.dataUUID,
        parent_constraint: this.zoomInfo.parentId ? this.zoomInfo.parentId : '',
        compiereRequest: {
          windowType: CompiereDataGridType.WINDOW,
          entityId: this.tabId,
          startRow: 0,
          endRow: 1,
          validation: this.getTabWhereclause(),
          windowCtx: this.getCurrentContext(),
        },
      };
      sub = this.#store.getWindowSingleData(request, false, true);
    }

    this.subscriptions.push(
      sub.subscribe((data: DataStore) => {
        this.buildChildren();
        if (newData) {
          if (this.zoomInfo.zoomRequest) {
            data.data['AD_Table_ID'] = this.zoomInfo.zoomRequest.ad_table_id;
            data.data['Record_ID'] = this.zoomInfo.zoomRequest.record_id;
          }
          this.updateEditTabsData(data);
        } else {
          this.updateSmartButton(data);
          this.updateNbFileAttached(data);
          this.updateNbRequest(data);
        }
      })
    );
  }

  refreshZoom(zoomInfo: IupicsZoomInfo): void {
    this.zoomInfo = zoomInfo;
    this.currentDataStoreKey = this.#store.generateDataStoreKey(
      this.zoomInfo.windowId,
      this.tabId,
      this.zoomInfo.dataUUID
    );
    this.retrieveData(this.currentDataStoreKey);
  }

  getCurrentContext(withCtx = true, onlyParent = false): any {
    let currentParent;
    if (this.linkedComponents?.[0]?.editViewParent?.editTabs?.[0]?.dataStored?.data) {
      currentParent = this.linkedComponents[0].editViewParent;
    }

    return EditViewUtils.getCurrentContext(
      currentParent, // parent
      onlyParent ? null : this.editTabs?.[0]?.dataStored, // store
      withCtx ? this.#connectorService.getIupicsUserContext() : {}
    );
  }

  showPriceHistory(gridTabInfinityScrollUiComponent: GridTabInfinityScrollUiComponent): void {
    if (!this.priceHistoryFormId) {
      const requestForm: DataStoreRequest = {
        windowId: null,
        parent_constraint: '',
        compiereRequest: {
          startRow: 0,
          endRow: 0,
          tableName: 'AD_Form',
          filterModel: {
            NAME: {
              filterType: CompiereDataGridFilterType.SET,
              values: ['Price history'],
              operators: [OperatorFilterType.EQUALS],
            },
          },
          windowCtx: this.getCurrentContext(),
        },
      };
      const $sub = this.#store.getDataGrid(requestForm, true).subscribe((formWS) => {
        this.priceHistoryFormId = formWS.data[0]['AD_FORM_ID'];
        this.showPriceHistoryForm(gridTabInfinityScrollUiComponent);
        $sub.unsubscribe();
      });
    } else {
      this.showPriceHistoryForm(gridTabInfinityScrollUiComponent);
    }
  }

  showPriceHistoryForm(gridTabInfinityScrollUiComponent: GridTabInfinityScrollUiComponent): void {
    if (gridTabInfinityScrollUiComponent?.grid?.api?.getSelectedRows()?.length > 0) {
      this.updateModalDisplay(
        ModalDisplayRequest.Form(true, this.priceHistoryFormId, gridTabInfinityScrollUiComponent)
      );
    } else {
      this.#messageManager.newMessage(
        new IupicsMessage(
          this.#translateService.instant('infodialog.dialogs.noLineSelected.title'),
          this.#translateService.instant('infodialog.dialogs.noLineSelected.message'),
          'warning'
        )
      );
    }
  }

  /**
   * permet d'overrider l'action si il existe un process ou une form. La méthode retourne true si c'est le cas.
   * @param source indique quelle action est à overrider
   */
  checkAndExecuteOverride(source: string): boolean {
    let formIdToOpen;
    let processIdToOpen;
    const adWindowId = this.currentDataStoreKey ? '' + this.currentDataStoreKey.windowId : '';

    switch (source) {
      case 'new':
        formIdToOpen = this.#config.getConstant(EditViewGetFormIDByTabID.OPEN_NEW + this.tabId);
        processIdToOpen = this.#config.getConstant(EditViewGetProcessIDByTabID.OPEN_NEW + this.tabId);
        if (formIdToOpen === undefined && processIdToOpen === undefined) {
          formIdToOpen = this.#config.getConstant(EditViewGetFormIDByWindowID.OPEN_NEW + adWindowId);
          processIdToOpen = this.#config.getConstant(EditViewGetProcessIDByWindowID.OPEN_NEW + adWindowId);
        }
        break;

      case 'copy':
        formIdToOpen = this.#config.getConstant(EditViewGetFormIDByTabID.COPY_DATA + this.tabId);
        processIdToOpen = this.#config.getConstant(EditViewGetProcessIDByTabID.COPY_DATA + this.tabId);
        if (formIdToOpen === undefined && processIdToOpen === undefined) {
          formIdToOpen = this.#config.getConstant(EditViewGetFormIDByWindowID.COPY_DATA + adWindowId);
          processIdToOpen = this.#config.getConstant(EditViewGetProcessIDByWindowID.COPY_DATA + adWindowId);
        }
        break;

      case 'export':
        formIdToOpen = this.#config.getConstant(EditViewGetFormIDByTabID.EXPORT_DATA + this.tabId);
        processIdToOpen = this.#config.getConstant(EditViewGetProcessIDByTabID.EXPORT_DATA + this.tabId);
        if (formIdToOpen === undefined && processIdToOpen === undefined) {
          formIdToOpen = this.#config.getConstant(EditViewGetFormIDByWindowID.EXPORT_DATA + adWindowId);
          processIdToOpen = this.#config.getConstant(EditViewGetProcessIDByWindowID.EXPORT_DATA + adWindowId);
        }
        break;

      case 'refresh':
        formIdToOpen = this.#config.getConstant(EditViewGetFormIDByTabID.REFRESH_DATA + this.tabId);
        processIdToOpen = this.#config.getConstant(EditViewGetProcessIDByTabID.REFRESH_DATA + this.tabId);
        if (formIdToOpen === undefined && processIdToOpen === undefined) {
          formIdToOpen = this.#config.getConstant(EditViewGetFormIDByWindowID.REFRESH_DATA + adWindowId);
          processIdToOpen = this.#config.getConstant(EditViewGetProcessIDByWindowID.REFRESH_DATA + adWindowId);
        }
        break;

      case 'delete':
        formIdToOpen = this.#config.getConstant(EditViewGetFormIDByTabID.DELETE_DATA + this.tabId);
        processIdToOpen = this.#config.getConstant(EditViewGetProcessIDByTabID.DELETE_DATA + this.tabId);
        if (formIdToOpen === undefined && processIdToOpen === undefined) {
          formIdToOpen = this.#config.getConstant(EditViewGetFormIDByWindowID.DELETE_DATA + adWindowId);
          processIdToOpen = this.#config.getConstant(EditViewGetProcessIDByWindowID.DELETE_DATA + adWindowId);
        }
        break;

      default:
        break;
    }

    if (formIdToOpen !== undefined) {
      this.updateModalDisplay(ModalDisplayRequest.Form(true, formIdToOpen, { columnName: source }));
    } else if (processIdToOpen !== undefined) {
      this.updateModalDisplay(ModalDisplayRequest.Process(true, formIdToOpen, source));
    }

    return formIdToOpen !== undefined || processIdToOpen !== undefined;
  }

  refreshEditViews(showMessage = true): void {
    const editViews = this.container.DOMChildrenComponent.slice().filter(
      (child: unknown) => child instanceof EditViewUiComponent
    ) as EditViewUiComponent[];
    this.subscriptions.push(
      this.#store.syncWithRemoteWindowData(editViews[0].currentDataStoreKey).subscribe(() => {
        for (let i = 0; i < editViews.length; i++) {
          if (i !== 0) {
            editViews[i].menuBarDetailComponent.updateButtonLists();
          }
          editViews[i].refreshData(showMessage);
        }
      })
    );
  }

  showPreview(additionalInfo: any): void {
    // files / index only drive the blade navigation, they are not component inputs
    const { files, index, ...fileInfo } = additionalInfo ?? {};
    this.previewFiles = Array.isArray(files) ? files : [];
    this.previewIndex = this.previewFiles.length > 1 && index >= 0 ? index : -1;
    if (this.previewIndex < 0) {
      this.previewFiles = [];
    }
    this.updateModalDisplay(ModalDisplayRequest.JoinFile(false));
    if (Global.isMobile()) {
      this.openDraggableWindow('PreviewDocComponent', fileInfo);
    } else {
      this.openSideBlade('PreviewDocComponent', fileInfo);
    }
  }

  /** Preview shown in the side blade, if any. */
  get sidePreview(): PreviewDocComponent | null {
    const instance = this.sideComponentRef?.instance;
    return instance instanceof PreviewDocComponent ? instance : null;
  }

  previewNavigate(step: number): void {
    const target = this.previewIndex + step;
    if (this.previewFiles.length < 2 || target < 0 || target >= this.previewFiles.length) {
      return;
    }
    this.previewIndex = target;
    this.openSideBlade('PreviewDocComponent', this.previewFiles[target]);
  }

  downloadSidePreview(): void {
    this.sidePreview?.download();
  }

  openSidePreviewInNewTab(): void {
    this.sidePreview?.openInNewTab();
  }

  onSideBladeKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    // keys typed in a field (notes editor, search...) belong to that field
    if (target?.closest?.('input, textarea, select, [contenteditable="true"]')) {
      return;
    }
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.closeSideBarAndBlade();
    } else if (this.sidePreview && this.previewFiles.length > 1 && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
      event.preventDefault();
      this.previewNavigate(event.key === 'ArrowLeft' ? -1 : 1);
    }
  }

  showNotes(): void {
    if (Global.isMobile()) {
      this.openDraggableWindow('NotesPanelUiComponent');
    } else {
      this.openSideBlade('NotesPanelUiComponent');
    }
  }

  showRequest(): void {
    let recordId: any = this.currentDataStoreKey.recordId.split(',');
    recordId = recordId.length > 1 ? (isNaN(parseInt(recordId[1], 10)) ? recordId[1] : parseInt(recordId[1], 10)) : -1;
    if (this.nbRequest > 0) {
      this.#uiCreatorService.getWindow(201).subscribe((tabUI) => {
        const item: DynamicComponent = {
          container: this.container,
          DOMParentComponent: this.container,
          linkedComponents: [this],
          component: 'EditViewUiComponent',
          cssClass: 'iupics-blade-content',
          isCssOnComponent: false,
          tabId: tabUI.tabId,
          windowId: 201,
          zoomTarget: {
            buttonId: 201,
            record_id: 'AD_Table_ID,' + this.data.AD_Table_ID + ',Record_ID,' + recordId,
            zoomRequest: { ad_table_id: this.data.AD_Table_ID, record_id: recordId },
          },
          zoomTargetData: [tabUI.editTabs[0]],
        };

        this.componentEmitter.emit({
          type: IupicsTypeEvent.showEditView,
          item: item,
        });
      });
    } else {
      const request: NbRequestRequest = {
        tableId: this.data.AD_Table_ID,
        recordId,
        salesRepId: this.getCurrentContext()['##AD_User_ID'],
        roleId: this.getCurrentContext()['#AD_Role_ID'],
      };
      const $sub = this.#requestService.getNbRequests(request).subscribe((nb) => {
        this.nbRequest = nb.data[0]['NBRQ'];
        $sub.unsubscribe();
        if (this.nbRequest === 0) {
          this.zoomInfo = {
            windowId: 201,
            dataUUID: 'R_Request_ID,-1',
            record_id: -1,
            children: null,
            isInCurrentWindow: true,
            zoomRequest: { ad_table_id: this.data.AD_Table_ID, record_id: recordId },
          };
          this.#uiCreatorService.getWindow(201).subscribe((tabUI) => {
            const item: DynamicComponent = {
              container: this.container,
              DOMParentComponent: this.container,
              linkedComponents: [this],
              component: 'EditViewUiComponent',
              cssClass: 'iupics-blade-content',
              isCssOnComponent: false,
              tabId: tabUI.tabId,
              windowId: 201,
              zoomInfo: this.zoomInfo,
              zoomRequest: { ad_table_id: this.data.AD_Table_ID, record_id: recordId },
            };
            this.componentEmitter.emit({
              type: IupicsTypeEvent.showEditView,
              item: item,
            });
          });
        } else {
          this.#uiCreatorService.getWindow(201).subscribe((tabUI) => {
            const item: DynamicComponent = {
              container: this.container,
              DOMParentComponent: this.container,
              linkedComponents: [this],
              component: 'EditViewUiComponent',
              cssClass: 'iupics-blade-content',
              isCssOnComponent: false,
              tabId: tabUI.tabId,
              windowId: 201,
              zoomTarget: {
                buttonId: 201,
                record_id: 'AD_Table_ID,' + this.data.AD_Table_ID + ',Record_ID,' + recordId,
                zoomRequest: { ad_table_id: this.data.AD_Table_ID, record_id: recordId },
              },
              zoomTargetData: [tabUI.editTabs[0]],
            };

            this.componentEmitter.emit({
              type: IupicsTypeEvent.showEditView,
              item: item,
            });
          });
        }
      });
    }
  }

  openInDraggableWindow(): void {
    if (Global.isMobile()) {
      this.sideBarVcr.detach();
    } else {
      this.sideBladeVcr.detach();
    }
    let options;
    const left = this.sideBladeContainerEl?.nativeElement.getBoundingClientRect().left + 'px';
    const top = this.sideBladeContainerEl?.nativeElement.getBoundingClientRect().top - 41 + 'px';
    const width = this.sideBladeContainerEl?.nativeElement.getBoundingClientRect().width + 'px';
    const height = this.sideBladeContainerEl?.nativeElement.getBoundingClientRect().height + 31 + 'px';
    if (this.sideComponentRef.instance instanceof PreviewDocComponent) {
      options = {
        iconClass: 'icon-attach3x',
        title: this.sideComponentRef.instance.fileName,
        left: left,
        top: top,
        width: width,
        height: height,
      };
    } else {
      options = {
        iconClass: 'icon-notes',
        title: `${this?.data?.label} - ${this.getInfoToDisplayInWindow()}`,
        left: left,
        top: top,
        width: width,
        height: height,
      };
    }
    this.#dynamicComponentManager.moveIntoDraggableWindow(options, this.sideComponentRef);
    this.closeSideBarAndBlade();
  }

  getInfoToDisplayInWindow(): string {
    if (this.editTabs[0]?.dataContainers) {
      const dataContainersFound = this.editTabs[0].dataContainers.filter((d) =>
        ['DocumentNo', 'Value', 'Name'].includes(d.data.columnName)
      );
      let dataContainerFound = dataContainersFound.find((d) => d.data.columnName === 'DocumentNo');
      if (dataContainerFound) {
        return `${dataContainerFound?.fieldValue}`;
      }
      dataContainerFound = dataContainersFound.find((d) => d.data.columnName === 'Name');
      if (dataContainerFound) {
        return `${dataContainerFound?.fieldValue}`;
      }
      dataContainerFound = dataContainersFound.find((d) => d.data.columnName === 'Value');
      if (dataContainerFound) {
        return `${dataContainerFound?.fieldValue}`;
      }
    }
    return `${this?.currentDataStoreKey?.recordId}`;
  }

  createOptions(type: string):
    | {
        table_id: number;
        currentDataStoreKey: DataStoreKey;
        parentEdit: EditViewUiComponent;
        parentTab: EditTabUiComponent;
      }
    | { parentComponent: EditViewUiComponent } {
    if (type === 'NotesPanelUiComponent') {
      return {
        table_id: this.table_id,
        currentDataStoreKey: this.currentDataStoreKey,
        parentEdit: this,
        parentTab: this.editTabs[0],
      };
    } else {
      return {
        parentComponent: this,
      };
    }
  }

  openSideBar(type: string, additionalInfo?: any): void {
    this.isSidebarOpen = true;
    let options = this.createOptions(type);
    if (additionalInfo) {
      options = { ...options, ...additionalInfo };
    }
    this.sideComponentRef = this.#dynamicComponentManager.clearAndCreateComponent(this.sideBarVcr, {
      type: type,
      options: options,
    });
  }

  closeSideBarAndBlade(): void {
    this.sideBarVcr?.clear();
    this.sideBladeVcr?.clear();
    this.sideComponentRef = null;
    this.previewFiles = [];
    this.previewIndex = -1;
    if (this.sideBladeContainerEl?.nativeElement) {
      this.#renderer.setStyle(this.sideBladeContainerEl.nativeElement, 'width', null);
    }
  }

  openSideBlade(type: string, additionalInfo?: any): void {
    if (type === 'PreviewDocComponent') {
      this.sideBladeTitle = additionalInfo.fileName;
      this.sideBladeIconClass = getFileTypeIconClass(additionalInfo.fileName);
    } else {
      this.sideBladeTitle = this.#translateService.instant('detailsNote.title');
      this.sideBladeIconClass = 'icon-notes';
    }
    if (this.sideBladeVcr != null) {
      if (this.sideBladeVcr.length > 0 && this.sideComponentRef?.instance instanceof getComponentType(type)) {
        if (this.sideComponentRef?.instance instanceof PreviewDocComponent) {
          this.sideComponentRef.instance.onPreview(null, additionalInfo.fileName, additionalInfo.urlFile);
        } else {
          this.closeSideBarAndBlade();
        }
      } else {
        let options = this.createOptions(type);
        if (additionalInfo) {
          options = { ...options, ...additionalInfo };
        }
        this.sideComponentRef = this.#dynamicComponentManager.clearAndCreateComponent(this.sideBladeVcr, {
          type: type,
          options: options,
        });
      }
    }
  }

  openDraggableWindow(type: string, additionalInfo?: any): void {
    let windowOptions;
    if (type === 'PreviewDocComponent') {
      windowOptions = {
        iconClass: 'icon-attach3x',
        title: additionalInfo.fileName,
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
      };
    } else {
      windowOptions = {
        iconClass: 'icon-notes',
        title: `${this?.data?.label} - ${this.getInfoToDisplayInWindow()}`,
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
      };
    }
    let options = this.createOptions(type);
    if (additionalInfo) {
      options = { ...options, ...additionalInfo };
    }
    const draggableWindow = this.#dynamicComponentManager.createDraggableWindow(windowOptions);
    this.#dynamicComponentManager.createComponent(draggableWindow.instance.contentVcr, {
      type: type,
      options: options,
    });
  }

  onMouseMove(event: MouseEvent): void {
    if (this.isResizing) {
      this.resizeSideBlade(event.clientX);
    }
  }

  onTouchMove(event: TouchEvent): void {
    if (this.isResizing) {
      const touchX = event.changedTouches[0].clientX;
      this.resizeSideBlade(touchX);
    }
  }

  onResizeSideBladeEnd(): void {
    if (this.isResizing) {
      this.isResizing = false;
      this.removeResizeListeners();
      this.scrollTo();
    }
  }

  startResizeSideBlade(event: Event): void {
    const el = this.sideBladeContainerEl?.nativeElement;
    const point = (event as TouchEvent).touches?.[0] ?? (event as MouseEvent);
    // anchor on the drag start: the blade's right edge moves when the edit view grows
    this.resizeStartX = point?.clientX ?? 0;
    this.resizeStartWidth = el ? el.getBoundingClientRect().width : 0;
    if (event instanceof MouseEvent) {
      event.preventDefault();
    }
    this.isResizing = true;
    // Document listeners only while resizing, outside the zone: moves only set a style, no change detection needed
    if (this.resizeUnlisteners.length === 0) {
      this.#zone.runOutsideAngular(() => {
        this.resizeUnlisteners = [
          this.#renderer.listen('document', 'mousemove', (e: MouseEvent) => this.onMouseMove(e)),
          this.#renderer.listen('document', 'touchmove', (e: TouchEvent) => this.onTouchMove(e)),
          this.#renderer.listen('document', 'mouseup', () => this.#zone.run(() => this.onResizeSideBladeEnd())),
          this.#renderer.listen('document', 'touchend', () => this.#zone.run(() => this.onResizeSideBladeEnd())),
        ];
      });
    }
  }

  private removeResizeListeners(): void {
    for (const unlisten of this.resizeUnlisteners) {
      unlisten();
    }
    this.resizeUnlisteners = [];
  }

  resizeSideBlade(x: number): void {
    if (this.isResizing) {
      if (this.sideBladeContainerEl) {
        // handle on the left edge: dragging left widens the blade (delta from drag start, no feedback loop)
        const newWidth = Math.max(0, this.resizeStartWidth + (this.resizeStartX - x));
        this.#renderer.setStyle(this.sideBladeContainerEl?.nativeElement, 'width', `${newWidth}px`);
      } else {
        console.log("couldn't resize");
      }
    }
  }

  getTabWhereclause(): string {
    let validation = '';
    const element = this.linkedComponents[0];
    if (element instanceof GridViewUiComponent) {
      validation = element.getTabWhereclause();
    }
    return validation;
  }

  showProcess(process: ToolbarProcess): void {
    // same rule as the button in the form: a read-only process cannot be run
    if (process?.readOnly) return;
    // the tab of the button (ids compared as text), else the top tab (button of a field group without tab id)
    const editTab =
      this.DOMChildrenComponent.find((dc) => process.tabId != null && String(dc.tabId) === String(process.tabId)) ??
      this.editTabs[0];
    let sourceComponent = undefined;
    for (let i = 0; i < (editTab?.DOMChildrenComponent?.length ?? 0) && sourceComponent === undefined; i++) {
      const child = editTab.DOMChildrenComponent[i];
      if (child.uuid === process.componentUUID) {
        // case field
        sourceComponent = child;
      } else if (child.DOMChildrenComponent.length) {
        // case accordion
        sourceComponent = child.DOMChildrenComponent.find((_child) => _child.uuid === process.componentUUID);
      }
    }
    this.updateModalDisplay(ModalDisplayRequest.Process(true, process.processId, sourceComponent));
  }
}

/** a chip of the section bar */
export interface EditViewSection {
  key: string;
  label: string;
  /** an empty mandatory field inside */
  todo: boolean;
  kind: 'top' | 'group' | 'tab';
  comp: EditTabUiComponent | AccordionUiComponent;
}
